/**
 * Contextual page help for the NETWORK and SYSTEM sections. Each page is
 * explained on two axes — the business (why it exists commercially) and the
 * software (what the screen does). Surfaced via the Help button in the topbar
 * on any route that has an entry here.
 *
 * Copy is written against THIS platform's current model (layered
 * Wholesale/Distribution/Retail pricing + the rule simulator; SIGN / Sale
 * Channel grants with two-way consent; reference-data + fine-grained RBAC),
 * not a generic OTA.
 */

export type HelpSectionName = 'Network' | 'System' | 'Supply' | 'Inventory' | 'Customers' | 'Reservations';
export interface HelpPoint { title: string; desc: string; }
export interface HelpSection { lead: string; points: HelpPoint[]; }
/** One line of a worked numeric example. `value` renders monospace on the
 *  right; `accent` tints it ('info' | 'success' | 'accent'); `strong` bolds the
 *  whole row (for a running/total line). */
export interface HelpExampleRow { label: string; value: string; strong?: boolean; accent?: string; }
/** A worked example card — a heading, numbered rows, and an optional note. */
export interface HelpExample { title: string; rows: HelpExampleRow[]; note?: string; }
/** One step in a booking / task flow. Rendered as an ordered list item. */
export interface HelpFlowStep { title: string; desc: string; }
/** "How you book on this screen" — a numbered end-to-end flow the operator
 *  can follow, distinct from the "what this screen does" software section. */
export interface HelpFlow { lead: string; steps: HelpFlowStep[]; }
/** One mode / variant that alters how the screen behaves — Instant vs
 *  On-Request for hotels, physical vs eSIM for SIMs, etc. `when` is a short
 *  "when to use it" hint rendered inline; `desc` is the definition. */
export interface HelpMode { name: string; when?: string; desc: string; }
/** "What each mode means" — a definition list of the modes / variants the
 *  screen surfaces, so an operator can pick the right one without guessing. */
export interface HelpModes { lead: string; modes: HelpMode[]; }
/** Help for one tab/section of a tabbed page — a focused description + items
 *  (+ optional worked examples) shown on top of the page's shared help when
 *  that tab is active. */
export interface TabHelp { title: string; lead: string; points?: HelpPoint[]; examples?: HelpExample[]; }
/** One status node in a lifecycle diagram. `kind` tints the chip (neutral = in
 *  progress, warn = awaiting an action, success = the sale is real, danger = an
 *  unhappy terminal). `note` is a short caption rendered under the chip. */
export interface HelpStatusNode { status: string; kind?: 'neutral' | 'warn' | 'success' | 'danger'; note?: string; }
/** A branch that leaves the happy path at `from` (e.g. Held → Expired, or an
 *  On-Request stay → In review → Confirmed / Rejected). Drawn as an indented
 *  offshoot under the main chip flow. */
export interface HelpStatusBranch { from: string; label?: string; nodes: HelpStatusNode[]; desc?: string; }
/** A status lifecycle for one product family — drawn left→right as chips joined
 *  by arrows, with optional branches. `success` names the state that means the
 *  sale is real (Ticketed for transport, Confirmed for everything else). */
export interface HelpLifecycle { product: string; success?: string; lead: string; path: HelpStatusNode[]; branches?: HelpStatusBranch[]; }
/** "Status lifecycle" help block — a shared lead, a colour legend, and one
 *  diagram per product family. `title` / `kicker` / `successPrefix` / `fromLabel`
 *  are the section's chrome strings; they default to English when a locale omits
 *  them, so a translation only needs to fill them to render fully localized. */
export interface HelpLifecycles {
  lead: string;
  title?: string;
  kicker?: string;
  successPrefix?: string;
  fromLabel?: string;
  legend?: { label: string; kind: 'neutral' | 'warn' | 'success' | 'danger' }[];
  items: HelpLifecycle[];
}
export interface PageHelp {
  title: string;
  tag: string;
  tagline: string;
  business: HelpSection;
  software: HelpSection;
  /** Optional step-by-step booking flow (rendered after "the software"). */
  flow?: HelpFlow;
  /** Optional mode / variant definitions (rendered after the flow). */
  modes?: HelpModes;
  /** Optional per-product status lifecycles, rendered as chip-flow diagrams. */
  lifecycles?: HelpLifecycles;
  /** Optional worked numerical examples (rendered after the modes). */
  examples?: HelpExample[];
  /** Optional per-tab help, keyed by the screen's tab id. When the drawer is
   *  opened with a matching `subKey`, that tab's focused help renders on top of
   *  the shared sections above. */
  tabs?: Record<string, TabHelp>;
  /** Keys of other pages with help — rendered as quick links. */
  related: string[];
}

/** Keys per section. Anything not listed here defaults to NETWORK. Drives the
 *  section label shown in the help drawer's chip + footer. */
export const SYSTEM_HELP_KEYS = new Set<string>([
  'reference-data', 'data-simulator', 'rbac', 'users', 'settings', 'issues', 'my-issues',
]);
export const SUPPLY_HELP_KEYS = new Set<string>([
  'supply-properties', 'supply-property-detail',
]);
/** Keys that belong to the INVENTORY section — the search-and-book funnel
 *  entries in the sidebar (flights, hotels, tours, transport, visa, cip,
 *  ground, sim, insurance). Router URL segments as they appear in the app. */
export const INVENTORY_HELP_KEYS = new Set<string>([
  'flights-search', 'hotels-search', 'tours', 'trains-search', 'buses-search',
  'visa', 'cip', 'ground', 'sim', 'insurance',
  // Search-to-ticket funnel pages (flights / trains / hotels / tours).
  'flights-results', 'flights-manual', 'flights-passengers', 'flights-seats', 'flights-payment', 'flights-ticket',
  'trains-results', 'trains-manual', 'trains-passengers', 'trains-payment', 'trains-ticket',
  'hotels-results', 'hotels-ticket',
  'tours-new',
]);
/** Keys that belong to the CUSTOMERS section — the CRM directory, customer
 *  groups and the loyalty club. Router URL segments as they appear in the app. */
export const CUSTOMERS_HELP_KEYS = new Set<string>([
  'customers', 'customer-groups', 'loyalty',
]);
/** Keys that belong to the RESERVATIONS section — the order book and its
 *  post-sale surfaces. Router URL segments as they appear in the app. */
export const RESERVATIONS_HELP_KEYS = new Set<string>([
  'bookings', 'booking-detail', 'queue', 'refunds', 'counter',
]);

export function helpSectionFor(key: string): HelpSectionName {
  if (SYSTEM_HELP_KEYS.has(key)) return 'System';
  if (SUPPLY_HELP_KEYS.has(key)) return 'Supply';
  if (INVENTORY_HELP_KEYS.has(key)) return 'Inventory';
  if (CUSTOMERS_HELP_KEYS.has(key)) return 'Customers';
  if (RESERVATIONS_HELP_KEYS.has(key)) return 'Reservations';
  return 'Network';
}

export const PAGE_HELP: Record<string, PageHelp> = {
  customers: {
    title: 'Customers',
    tag: 'CRM directory',
    tagline: 'The master record of the travelers and companies an agency sells to.',
    business: {
      lead: 'Customers are who the agency actually sells to — leisure travelers, corporate accounts and the companions who travel with them. Keeping one clean record per customer is what lets the platform prefill bookings, price a segment differently, run a loyalty club, and bill the right party. A customer that is billable also becomes an accounting contact (طرف حساب) downstream.',
      points: [
        { title: 'One source of truth', desc: 'Name, document, contact and consent live on the customer record — booking screens read from it, so a traveler is entered once and reused everywhere.' },
        { title: 'Segments drive price', desc: 'A customer can belong to a customer group; the Retail layer of Markup & Rules can then price that group differently — a discount for VIPs, a fee for walk-ins.' },
        { title: 'Consent is recorded', desc: 'Marketing opt-in and data-processing consent are stored per customer so outreach stays compliant.' },
      ],
    },
    software: {
      lead: 'A searchable roster of every customer, with the contact, loyalty and consent columns you need to manage them.',
      points: [
        { title: 'Find & filter', desc: 'Search by name, phone, email or national ID; open a row for the full profile.' },
        { title: 'Edit the profile', desc: 'The edit drawer captures identity, phone/WhatsApp (split dial-code + number), nationality, preferred channel and language, VIP tier and internal notes; add companions who travel with them.' },
        { title: 'Loyalty at a glance', desc: 'Each profile shows the customer’s points balance, tier and history, with manual earn / redeem / adjust.' },
      ],
    },
    related: ['customer-groups', 'loyalty', 'markup', 'agencies'],
  },

  'customer-groups': {
    title: 'Customer Groups',
    tag: 'Segmentation',
    tagline: 'Tag customers into segments so the Retail pricing layer can treat them differently.',
    business: {
      lead: 'A customer group is a named segment — “VIPs”, “Corporate — Acme”, “Walk-ins” — that you can price against. It’s the customer-side counterpart of an agency group: where agency groups scope Wholesale/Distribution rules, customer groups scope the Retail layer, so the selling agency can reward or surcharge a slice of its own customers.',
      points: [
        { title: 'A lever for retail margin', desc: 'Group membership is what a Retail rule targets — e.g. a −3% loyalty discount for the VIP group, or a service fee for unverified walk-ins.' },
        { title: 'Membership, not hierarchy', desc: 'A customer simply belongs to a group; there’s no nesting. One clear tag keeps pricing predictable.' },
        { title: 'Owned by the agency', desc: 'Each agency defines its own groups over its own customers — they don’t leak across the network.' },
      ],
    },
    software: {
      lead: 'Create groups and assign customers, then reference the group from a Retail pricing rule’s Group scope.',
      points: [
        { title: 'Create & name a group', desc: 'Define a group with a code and name; activate or archive it as segments evolve.' },
        { title: 'Assign members', desc: 'Add or remove customers from a group; the count surfaces on the group and on Markup & Rules’ Group-scoped rows.' },
        { title: 'Wire it to pricing', desc: 'In Markup & Rules, pick partner-scope = Group on a Retail rule and choose this group — every matching booking for its members takes the rule.' },
      ],
    },
    examples: [
      {
        title: 'A VIP group discount on the Retail layer',
        rows: [
          { label: 'Traveler subtotal (before retail rule)', value: '11,024,000' },
          { label: 'Retail DISCOUNT −3% · group = VIPs', value: '−330,720', accent: 'success', strong: true },
          { label: 'VIP customer pays', value: '10,693,280' },
        ],
        note: 'The same booking for a non-VIP customer skips the rule and pays 11,024,000. Group membership is the only thing that differs.',
      },
      {
        title: 'A walk-in service fee — the other direction',
        rows: [
          { label: 'Traveler subtotal', value: '11,024,000' },
          { label: 'Retail SERVICE_FEE +150,000 · group = Walk-ins', value: '+150,000', accent: 'accent', strong: true },
          { label: 'Walk-in customer pays', value: '11,174,000' },
        ],
        note: 'A flat SERVICE_FEE surcharges an unverified segment. Flat rules ignore the fare size — every walk-in booking adds the same 150,000, whatever the ticket price.',
      },
      {
        title: 'Corporate net rate — a group can also unlock a negotiated deal',
        rows: [
          { label: 'Standard traveler subtotal', value: '11,024,000' },
          { label: 'Retail DISCOUNT −6% · group = Corporate · Acme', value: '−661,440', accent: 'success', strong: true },
          { label: 'Acme’s employee pays', value: '10,362,560' },
        ],
        note: 'A deeper group discount encodes a corporate contract: everyone tagged into “Corporate · Acme” automatically gets Acme’s negotiated 6% off — no per-booking overrides.',
      },
    ],
    related: ['customers', 'markup', 'agency-groups', 'loyalty'],
  },

  loyalty: {
    title: 'Loyalty Club',
    tag: 'Voyager Rewards',
    tagline: 'The agency’s points programme — how travelers earn, climb tiers and redeem.',
    business: {
      lead: 'A loyalty club turns one-off travelers into repeat ones. Customers earn points as they book, climb tiers that unlock perks and better earn rates, and redeem points against future bookings. The outstanding balance is a real liability the agency carries, so the club is run with explicit earn and redemption rates.',
      points: [
        { title: 'Retention, quantified', desc: 'Points and tiers give a customer a reason to book again with you rather than a competitor.' },
        { title: 'Tiers by lifetime earned', desc: 'A customer’s tier reflects lifetime earned points, so redeeming never demotes them — Bronze → Silver → Gold → Platinum, each with a higher earn multiplier.' },
        { title: 'Points are a liability', desc: 'Every unredeemed point is money owed; the club overview surfaces total points and the currency liability so it stays on the books.' },
      ],
    },
    software: {
      lead: 'A club overview plus the two things that run it: settings (rates + trigger) and the auto-earn rules.',
      points: [
        { title: 'Overview KPIs', desc: 'Members, total points, liability and the per-tier distribution — computed live from the ledger.' },
        { title: 'Club settings', desc: 'Set the earn rate, redemption rate (points per unit), points expiry, and the auto-earn trigger — award on ISSUE (fulfilled) or on PAID (payment captured).' },
        { title: 'Earn rules', desc: 'Author per-product points rules (e.g. Flights earn 2× per unit); a booking’s completion awards points from the matching rule automatically. Per-customer earn / redeem / adjust is on each profile.' },
      ],
    },
    modes: {
      lead: 'Two separate tier concepts exist — don’t confuse them.',
      modes: [
        { name: 'Loyalty tier', when: 'earned', desc: 'Bronze / Silver / Gold / Platinum, derived from lifetime earned points. Drives the earn multiplier and perks. Never set by hand.' },
        { name: 'VIP tier', when: 'manual', desc: 'A separate flag an agent sets on the customer record (Standard / Silver / Gold / VIP) for service handling — independent of loyalty points.' },
      ],
    },
    examples: [
      {
        title: 'Tier thresholds — set by lifetime earned points',
        rows: [
          { label: 'Bronze · 0+ earned', value: '1× earn' },
          { label: 'Silver · 2,000+ earned', value: '1.25× earn' },
          { label: 'Gold · 6,000+ earned', value: '1.5× earn', accent: 'info' },
          { label: 'Platinum · 15,000+ earned', value: '2× earn', accent: 'success' },
        ],
        note: 'Tier reads lifetime earned, so spending points never demotes a customer.',
      },
      {
        title: 'Auto-earn on a booking, and a tier jump',
        rows: [
          { label: 'Lifetime earned before (Silver)', value: '5,600' },
          { label: 'Flights rule · 2 pts per unit × 250', value: '+500', accent: 'info', strong: true },
          { label: 'Lifetime earned after → Gold', value: '6,100' },
        ],
        note: 'Points post automatically when the booking hits the club’s earn trigger (ISSUE or PAID). Crossing 6,000 promotes the customer to Gold — 1.5× earn from then on.',
      },
      {
        title: 'Redeeming points (default 100 pts = 1 unit off)',
        rows: [
          { label: 'Points balance', value: '8,000' },
          { label: 'Redeem 5,000 pts → discount', value: '−50', accent: 'success' },
          { label: 'Remaining balance', value: '3,000', strong: true },
        ],
        note: 'The redemption rate is set in Club settings; the balance is spendable points, distinct from lifetime earned that sets the tier.',
      },
      {
        title: 'Club liability — what the outstanding points are worth',
        rows: [
          { label: 'Total unredeemed points across members', value: '2,400,000' },
          { label: '÷ redemption rate (100 pts = 1)', value: '÷ 100' },
          { label: 'Currency liability on the books', value: '24,000', accent: 'accent', strong: true },
        ],
        note: 'The overview surfaces this so the club is never a hidden cost. Raising the redemption rate (more points per unit) lowers the liability but makes points earn less for the customer — the lever to balance.',
      },
    ],
    related: ['customers', 'customer-groups', 'markup'],
  },

  agencies: {
    title: 'Agencies',
    tag: 'Directory',
    tagline: 'The master directory of the B2B travel businesses that trade on the platform.',
    business: {
      lead: 'Agencies are the platform’s B2B customers — the travel companies you sell inventory to. Each carries the commercial and compliance terms that govern how it trades: its tier, its wallet or credit line, and its KYC standing.',
      points: [
        { title: 'Tiers set the terms', desc: 'An agency’s tier is the lever behind its pricing — it feeds the Tier-scoped rules in the Markup & Rules engine, so volume can be rewarded with better margin.' },
        { title: 'Wallet & credit are the trading limit', desc: 'An agency books against a prepaid wallet or an approved credit line; that balance is what caps the platform’s exposure.' },
        { title: 'KYC gates trading', desc: 'A new agency only goes live once KYC is approved — the status column shows, per row, who is trading and who is blocked.' },
      ],
    },
    software: {
      lead: 'A searchable roster of every agency, with the commercial and compliance columns you need to manage them at a glance.',
      points: [
        { title: 'Scan & filter', desc: 'Search by name, contact or code and filter by tier or status to find who matters.' },
        { title: 'Read the health columns', desc: 'Wallet balance, credit utilisation and KYC status surface per row.' },
        { title: 'Open a record', desc: 'Click a row to open the agency profile — edit its terms, SIGNs, sale channels, roles and access.' },
      ],
    },
    related: ['agency-profile', 'agents', 'agency-onboarding', 'markup'],
  },

  markup: {
    title: 'Markup & Rules',
    tag: 'Pricing engine',
    tagline: 'Where every layer of margin — from SIGN owner to reseller to traveler — is defined.',
    business: {
      lead: 'This is how a price is built from a supplier’s net fare up to what the traveler pays. The engine is layered: a Wholesale markup (the SIGN owner charges a reseller), a Distribution fee (a channel owner charges the agency selling through its channel), and a Retail margin (the selling agency’s own markup to the customer). Each layer is a different party’s money.',
      points: [
        { title: 'Three layers, three relationships', desc: 'Wholesale = SIGN owner → reseller; Distribution = channel owner → seller; Retail = seller → traveler. A rule always belongs to exactly one layer.' },
        { title: 'Targeted by segment', desc: 'Rules narrow by product, supplier, airline, route, cabin, SIGN or channel, partner tier or group, or a specific partner agency — so you can price precisely.' },
        { title: 'Guardrails protect price', desc: 'Min / max caps, priority and an active / inactive status stop a bad rule from making a fare uncompetitive or unprofitable.' },
      ],
    },
    software: {
      lead: 'A rules engine resolved by specificity — the most-specific matching rule wins within each layer.',
      points: [
        { title: 'Specificity order', desc: 'Partner → Tier → SIGN / Channel → Route → Owner default. The resolver walks that ladder for each layer of a booking.' },
        { title: 'Scoring is how the resolver picks', desc: 'Every non-wildcard match dimension a rule sets contributes a fixed weight to its score. Winner per layer = highest score. Tie → higher `priority` wins; still a tie → the more recently updated rule wins. The simulator shows the score on each candidate card so you can see exactly why one rule beat another.' },
        { title: 'Build & filter rules', desc: 'Author a rule’s layer, adjustment type, value (percent / flat / tiered), base and scope; filter the growing list by type, product, partner scope or status.' },
        { title: 'Simulate before it’s live', desc: 'The built-in simulator prices a hypothetical booking and shows exactly which rule fires per layer — with no risk to live pricing.' },
      ],
    },
    examples: [
      {
        title: 'Specificity weights — what each match dim is worth',
        rows: [
          { label: 'Partner = specific AGENCY', value: '100' },
          { label: 'SIGN (wholesale) · Channel (distribution)', value: '90' },
          { label: 'Origin & Destination — airport / station', value: '70' },
          { label: 'Partner GROUP', value: '60' },
          { label: 'Origin & Destination — city', value: '50' },
          { label: 'Partner TIER', value: '40' },
          { label: 'Region', value: '35' },
          { label: 'Supplier · Airline · Fare type', value: '30 each' },
          { label: 'Cabin', value: '25' },
          { label: 'Product', value: '20' },
        ],
        note: 'A rule\'s score is the sum of the weights of every non-wildcard dimension that actually matched the booking. Wildcards (blank) don\'t score — they just don\'t exclude.',
      },
      {
        title: 'Two rules compete — highest score wins',
        rows: [
          { label: 'Booking: seller = Nomad (GOLD tier), product = flight', value: '' },
          { label: 'Rule A · WHOLESALE · TIER=GOLD + product=flight', value: '40 + 20 = 60', accent: 'info' },
          { label: 'Rule B · WHOLESALE · AGENCY=Nomad', value: '100', accent: 'success' },
          { label: 'Winner', value: 'Rule B', strong: true },
        ],
        note: 'Both rules match this booking. Rule B\'s partner-agency lock scores 100 on its own — beats Rule A\'s tier+product combo (60). Partner-specific is the most specific dimension the engine knows.',
      },
      {
        title: 'Same score — priority breaks the tie',
        rows: [
          { label: 'Booking: product=flight, cabin=BUSINESS, supplier=Amadeus', value: '' },
          { label: 'Rule X · product+cabin (20+25) · priority=0', value: 'score 45 · pri 0' },
          { label: 'Rule Y · product+supplier (20+30) · priority=10', value: 'score 50 · pri 10', accent: 'success' },
          { label: 'Winner', value: 'Rule Y (higher score)', strong: true },
        ],
        note: 'Y wins on raw score (50 > 45). If two rules ever tied on score, `priority` breaks it — and if `priority` also ties, the more recently updated rule wins. That last tiebreak means editing an existing rule effectively refreshes it to the top of an otherwise-tied group.',
      },
      {
        title: 'Wholesale — SIGN owner → reseller (on the net fare)',
        rows: [
          { label: 'Airline net', value: '10,000,000' },
          { label: 'MARKUP +4% of net', value: '+400,000', accent: 'info' },
          { label: 'Reseller’s cost', value: '10,400,000', strong: true },
        ],
      },
      {
        title: 'Retail — seller → traveler (on the running gross)',
        rows: [
          { label: 'Running total', value: '10,400,000' },
          { label: 'MARKUP +6% of gross', value: '+624,000', accent: 'success' },
          { label: 'Traveler subtotal', value: '11,024,000', strong: true },
        ],
      },
      {
        title: 'Distribution — channel fee (add-on vs rev-share)',
        rows: [
          { label: 'Add-on +2% on 11,024,000', value: '+220,480', accent: 'accent' },
          { label: 'Rev-share 2% (out of margin)', value: 'price unchanged' },
        ],
        note: 'Add-on is charged on top of the fare; rev-share leaves the traveler price alone and deducts the channel’s cut from the seller’s margin.',
      },
      {
        title: 'Value modes — basis 10,000,000, booking of 3 pax / 2 segments',
        rows: [
          { label: 'Percent · 5% × 10,000,000', value: '500,000' },
          { label: 'Flat · 150,000 per booking', value: '150,000' },
          { label: 'Flat · 150,000 per pax × 3', value: '450,000' },
          { label: 'Flat · 150,000 per segment × 2', value: '300,000' },
          { label: 'Percent + flat · 3% + 200,000', value: '500,000' },
        ],
        note: 'Any layer can use any mode. Adjustment type sets the sign: MARKUP / SERVICE_FEE add; DISCOUNT / REBATE subtract.',
      },
      {
        title: 'End-to-end waterfall — partner issues under a SIGN, then resells',
        rows: [
          { label: 'Supplier net (airline)', value: '10,000,000' },
          { label: '+ Wholesale MARKUP 4%', value: '+400,000', accent: 'info' },
          { label: '+ Retail MARKUP 6%', value: '+624,000', accent: 'success' },
          { label: '+ Distribution add-on 2%', value: '+220,480', accent: 'accent' },
          { label: 'Traveler price', value: '11,244,480', strong: true },
        ],
        note: 'Exactly what the Simulator computes. With rev-share instead of add-on, the traveler still pays 11,024,000 and the channel’s 220,480 comes off the seller’s margin.',
      },
    ],
    related: ['agency-groups', 'sign-access', 'channel-access', 'agencies'],
  },

  'partner-access': {
    title: 'Partner Access',
    tag: 'Pairing',
    tagline: 'How agencies pair up to share issuing credentials (SIGNs) and sale channels.',
    business: {
      lead: 'Not every agency holds the airline credentials (SIGNs) or the sale channels it needs to ticket everything it can sell. Partner Access lets two agencies connect and share those assets — so one can issue on another’s SIGN or sell through its channel — widening what both can sell without either buying new inventory.',
      points: [
        { title: 'Borrow reach, keep control', desc: 'The owner grants access and can revoke it at any time — sharing is a permission, not a transfer of the asset.' },
        { title: 'Both parties must agree', desc: 'Pairing and every grant is two-way consent: an offer or request the other side accepts before any asset is shared.' },
        { title: 'Two directions of value', desc: 'You track what you share out to partners and what you use from them as two separate ledgers.' },
      ],
    },
    software: {
      lead: 'This screen manages pairings and the SIGN / Sale Channel grants that flow across them.',
      points: [
        { title: 'Connect by agency code', desc: 'Send a partnership request using a partner’s unique agency code; they accept or decline it.' },
        { title: 'Grant SIGNs & Sale Channels', desc: 'Offer a SIGN (airline issuing credential) or a Sale Channel to a partner, scoped and time-boxed; the grantee accepts to activate it.' },
        { title: 'Manage both ledgers', desc: 'Review the access you’ve granted and the access you’ve received, and revoke it when a relationship ends.' },
      ],
    },
    examples: [
      {
        title: 'You share a SIGN — you earn the wholesale markup on every partner ticket',
        rows: [
          { label: 'Partner issues an EK ticket · airline net', value: '10,000,000' },
          { label: 'Your Wholesale markup 4% (they pay you)', value: '+400,000', accent: 'info', strong: true },
          { label: 'Partner’s cost for the ticket', value: '10,400,000' },
        ],
        note: 'You never touch the booking — sharing your SIGN earns you 400,000 per ticket the partner issues under it. They keep whatever retail margin they add for their own customer.',
      },
      {
        title: 'You use a partner’s SIGN — pay their wholesale, keep your retail',
        rows: [
          { label: 'You issue an EK ticket on their SIGN · net', value: '10,000,000' },
          { label: '+ Their Wholesale markup 4% → your cost', value: '10,400,000' },
          { label: '+ Your Retail markup 6% to your customer', value: '+624,000', accent: 'success' },
          { label: 'Your customer pays', value: '11,024,000', strong: true },
        ],
        note: 'Borrowing a SIGN lets you sell an airline you don’t hold. You pay the owner’s wholesale markup and keep your own 624,000 retail margin.',
      },
      {
        title: 'You share a Sale Channel — rev-share vs add-on',
        rows: [
          { label: 'Partner sells a 10,000,000 fare via your channel', value: '10,000,000' },
          { label: 'Rev-share 2% (your cut, out of their margin)', value: '+200,000', accent: 'accent', strong: true },
          { label: 'Add-on 2% (charged on top → traveler pays)', value: '10,200,000' },
        ],
        note: 'Rev-share earns you 200,000 from the seller’s margin with the traveler price unchanged; add-on adds your fee on top of the fare. You set which, per grant.',
      },
    ],
    related: ['sign-access', 'channel-access', 'signs', 'sale-channels'],
  },

  agents: {
    title: 'Sub-Agencies',
    tag: 'Downline',
    tagline: 'The resellers that sit under a master agency and sell on its behalf.',
    business: {
      lead: 'Sub-agencies extend distribution without the platform onboarding everyone directly. A master agency recruits them, sets their commercial terms, and stands behind their trading — turning one relationship into a selling chain.',
      points: [
        { title: 'Reach without overhead', desc: 'Every sub-agency is a new point of sale the platform never had to sign or fund individually.' },
        { title: 'Margin is layered', desc: 'The master agency can add its own Retail markup on top of the platform’s price before the sub-agency sees a fare — that spread is its business.' },
        { title: 'Consolidated accountability', desc: 'Wallet, credit and settlement roll up to the parent, so the platform settles with one counterparty.' },
      ],
    },
    software: {
      lead: 'Lists everyone reporting into an agency, with the controls that govern what each one can do.',
      points: [
        { title: 'Per-agency configuration', desc: 'Markup overrides, allowed products and access are set on each sub-agency from its record.' },
        { title: 'Roll-up totals', desc: 'Bookings and revenue from the downline aggregate up to the parent and the network total.' },
        { title: 'Admin vs agency view', desc: 'Admins see the whole network; an agency signed in sees only its own downline.' },
      ],
    },
    related: ['agencies', 'markup', 'partner-access'],
  },

  companies: {
    title: 'Companies',
    tag: 'Corporate parent',
    tagline: 'The legal entities that own sale channels and group agencies under one roof.',
    business: {
      lead: 'A company is the corporate parent above agencies — the entity that can own sale channels and therefore earn the Distribution layer’s fees. Grouping brands or agencies under one company keeps ownership of shared commercial assets and settlement in one place.',
      points: [
        { title: 'Owns the distribution assets', desc: 'Companies hold sale channels (not SIGNs); a company’s Distribution rules price what flows through those channels.' },
        { title: 'One legal counterparty', desc: 'Multiple agencies or brands can sit under a single company for contracting and settlement.' },
        { title: 'Separation of concerns', desc: 'Airline issuing (SIGNs) lives with agencies; channel distribution lives with the company.' },
      ],
    },
    software: {
      lead: 'The directory of companies and the channels they own.',
      points: [
        { title: 'Manage company records', desc: 'Create and edit companies and see the agencies and channels attached to each.' },
        { title: 'Channel ownership', desc: 'A company’s sale channels are managed from Sale channels and priced from Markup & Rules (Distribution layer).' },
        { title: 'Scope in pickers', desc: 'Company owners appear in the Markup & Rules owner picker as “channels only”.' },
      ],
    },
    related: ['sale-channels', 'channel-access', 'agencies'],
  },

  'sale-channels': {
    title: 'Sale channels',
    tag: 'Distribution',
    tagline: 'The storefronts and pipes inventory is sold through.',
    business: {
      lead: 'A sale channel is a route to market — a website, app, API integration or offline desk — owned by an agency or a company. It’s the asset the Distribution layer charges against: whoever owns the channel can take a fee or revenue share on what sells through it.',
      points: [
        { title: 'A channel is a revenue point', desc: 'Sales flowing through a channel can carry a Distribution fee or revenue share for the channel owner.' },
        { title: 'Own or borrow', desc: 'You sell through your own channels, or through a partner’s via a Channel access grant.' },
        { title: 'Shape the split', desc: 'A channel’s fee can be an add-on (on top of the fare) or a rev-share (off the seller’s margin).' },
      ],
    },
    software: {
      lead: 'The directory of sale channels with their owner and settlement shape.',
      points: [
        { title: 'Create & configure', desc: 'Register a channel, set its owner (agency or company) and its default revenue share / shape.' },
        { title: 'Grant to partners', desc: 'Share a channel out through Channel access so a partner agency can sell through it.' },
        { title: 'Feeds pricing', desc: 'Distribution rules in Markup & Rules target a specific channel or all of them.' },
      ],
    },
    related: ['channel-access', 'companies', 'partner-access', 'markup'],
  },

  'channel-access': {
    title: 'Channel access',
    tag: 'Distribution grant',
    tagline: 'Letting a partner agency sell through your sale channel.',
    business: {
      lead: 'Channel access is how a sale channel is lent to another agency. The owner grants a partner the right to sell through its channel — usually for a revenue share — widening distribution without handing over the asset. This is the granular grant behind the Distribution pricing layer.',
      points: [
        { title: 'Reach, on a revenue share', desc: 'The grantee sells through the channel; the owner keeps a share of the gross that flows through it.' },
        { title: 'Two-way consent', desc: 'A grant is offered and accepted — nothing is shared until both sides agree — and can be revoked any time.' },
        { title: 'Drives Distribution rules', desc: 'The grant’s revenue share is the legacy fallback; explicit Distribution rules in Markup & Rules override it.' },
      ],
    },
    software: {
      lead: 'The ledger of channel grants — given and received.',
      points: [
        { title: 'Grant & scope', desc: 'Offer a channel to a partner with a revenue share, shape and optional validity window.' },
        { title: 'Accept / decline', desc: 'The grantee accepts to activate the access, or declines it.' },
        { title: 'Revoke', desc: 'End access when a relationship ends; in-flight bookings settle on the terms in force.' },
      ],
    },
    related: ['sale-channels', 'partner-access', 'markup', 'companies'],
  },

  'sign-access': {
    title: 'SIGN access',
    tag: 'Wholesale grant',
    tagline: 'Letting a partner agency issue tickets under your SIGN.',
    business: {
      lead: 'SIGN access lends an issuing credential (a SIGN) to another agency so it can ticket a supplier it doesn’t hold credentials for. The SIGN owner grants the access, sets a markup toward that grantee, and can revoke it — this is the grant behind the Wholesale pricing layer.',
      points: [
        { title: 'Issue on borrowed credentials', desc: 'The grantee ticket-issues under the owner’s SIGN without registering its own BSP/IATA credential.' },
        { title: 'Markup toward the grantee', desc: 'The owner sets a per-grant wholesale markup — its margin for lending the SIGN.' },
        { title: 'Two-way consent', desc: 'A grant is offered and accepted, and can be revoked; sharing is a permission, not a transfer.' },
      ],
    },
    software: {
      lead: 'The ledger of SIGN grants — given and received.',
      points: [
        { title: 'Grant & set markup', desc: 'Offer a SIGN to a partner with an optional markup override and validity window.' },
        { title: 'Accept / decline', desc: 'The grantee accepts to start issuing on the SIGN, or declines.' },
        { title: 'Feeds Wholesale rules', desc: 'The grant markup is the legacy fallback; explicit Wholesale rules in Markup & Rules (AGENCY / SIGN scope) override it.' },
      ],
    },
    related: ['signs', 'partner-access', 'markup', 'agencies'],
  },

  signs: {
    title: 'SIGNs',
    tag: 'Issuing credentials',
    tagline: 'The BSP / IATA / CASS credentials an agency uses to issue tickets.',
    business: {
      lead: 'A SIGN is the registered credential that lets an agency issue tickets against a specific supplier. It’s the asset at the heart of the Wholesale layer: the SIGN owner can issue directly, or lend the SIGN to partners (SIGN access) and mark up what they issue.',
      points: [
        { title: 'The right to ticket', desc: 'Without a SIGN — or borrowed access to one — an agency can sell but not issue against that supplier.' },
        { title: 'One active per supplier', desc: 'An agency holds one active SIGN per supplier; superseded ones are retired, not deleted.' },
        { title: 'The wholesale asset', desc: 'Lending a SIGN and marking up the grantee’s issuance is the Wholesale layer’s business.' },
      ],
    },
    software: {
      lead: 'The register of an agency’s SIGNs, with their supplier, code, issuer and validity.',
      points: [
        { title: 'Register a SIGN', desc: 'Record the supplier, SIGN code, issuer and BSP account with a validity window.' },
        { title: 'Owned by an agency', desc: 'SIGNs are always owned by an agency (companies own channels, not SIGNs).' },
        { title: 'Share via SIGN access', desc: 'Lend a SIGN to a partner from SIGN access; revoke when the relationship ends.' },
      ],
    },
    related: ['sign-access', 'partner-access', 'markup', 'agencies'],
  },

  branches: {
    title: 'Branches',
    tag: 'Org structure',
    tagline: 'An agency’s sub-offices, used to scope staff and bookings.',
    business: {
      lead: 'Branches model an agency’s physical or organisational sub-offices. They let a larger agency organise its people and attribute bookings and performance to the office that made them, without splitting into separate agencies.',
      points: [
        { title: 'Attribution', desc: 'Bookings and revenue can be reported per branch, so you see which office is performing.' },
        { title: 'Staff scoping', desc: 'Users are assigned to a branch, keeping their view and permissions to their office.' },
        { title: 'One agency, many offices', desc: 'Branches keep a multi-office agency as a single trading relationship.' },
      ],
    },
    software: {
      lead: 'The directory of an agency’s branches.',
      points: [
        { title: 'Create & edit', desc: 'Add branches with their name, contact and location details.' },
        { title: 'Assign staff', desc: 'Branch is used when provisioning users and roles under the agency.' },
        { title: 'Admin vs agency view', desc: 'An admin manages any agency’s branches; an agency manages its own.' },
      ],
    },
    related: ['agencies', 'agency-profile'],
  },

  'agency-groups': {
    title: 'Agency groups',
    tag: 'Segmentation',
    tagline: 'Named buckets of agencies you can target with one pricing rule.',
    business: {
      lead: 'Agency groups are segmentation buckets — “Umrah specialists”, “Tehran corporates” — that let you price a whole set of agencies together instead of one by one. They power the GROUP partner scope in the pricing engine.',
      points: [
        { title: 'Price by segment', desc: 'A group is a target for a Markup & Rules rule with partner scope = Group.' },
        { title: 'Membership is many-to-many', desc: 'An agency can belong to several groups; a group holds many agencies.' },
        { title: 'Owner-scoped', desc: 'A group is platform-shared or owned by an agency, and follows a status lifecycle (never hard-deleted).' },
      ],
    },
    software: {
      lead: 'The management surface for groups and their memberships.',
      points: [
        { title: 'Create & curate', desc: 'Define a group and add or remove member agencies.' },
        { title: 'Target in pricing', desc: 'The group appears in the rule editor’s Group partner-scope picker.' },
        { title: 'Lifecycle, not deletion', desc: 'Retire a group by status so existing rules that reference it stay resolvable.' },
      ],
    },
    related: ['markup', 'agencies'],
  },

  'agency-onboarding': {
    title: 'Onboard an agency',
    tag: 'Wizard',
    tagline: 'Bringing a new travel business onto the network, compliantly.',
    business: {
      lead: 'Onboarding is the controlled front door to the network. Before a new agency can move money or issue tickets, the platform must know who they are, verify them, and agree commercial terms — this is where that relationship is created.',
      points: [
        { title: 'Compliance before trading', desc: 'Identity and financial (KYC) checks happen up front because the platform extends credit and settlement to a new counterparty.' },
        { title: 'Terms set the relationship', desc: 'Tier, markup profile and product access are agreed here — they define how the agency trades from day one.' },
        { title: 'Fast time-to-first-sale', desc: 'A clean onboarding gets a revenue-generating partner live within the onboarding SLA.' },
      ],
    },
    software: {
      lead: 'A guided multi-step form that assembles the agency record and routes it for approval.',
      points: [
        { title: 'Step through the record', desc: 'Company → contacts → KYC documents → financial setup → products & access → tier & markup → review.' },
        { title: 'Submit to the queue', desc: 'Completing the wizard files the application into the KYC review queue rather than going live immediately.' },
        { title: 'Everything in one place', desc: 'Contacts, documents and commercial terms are captured together so reviewers have the full picture.' },
      ],
    },
    related: ['agency-kyc', 'contract-templates', 'agencies'],
  },

  'agency-kyc': {
    title: 'KYC review queue',
    tag: 'Compliance',
    tagline: 'The gate every new agency passes before it can trade.',
    business: {
      lead: 'KYC — Know Your Customer — is the obligation to verify a business’s identity before letting it handle money on the platform. This queue is the checkpoint that protects the network from fraud and bad-debt exposure.',
      points: [
        { title: 'A requirement, not a formality', desc: 'The platform must verify counterparties before extending wallets or credit.' },
        { title: 'Risk, made visible', desc: 'Each application carries a risk signal so reviewers spend time where exposure is highest.' },
        { title: 'The SLA is a promise', desc: 'Clearing applications quickly keeps onboarding fast without skipping the check.' },
      ],
    },
    software: {
      lead: 'A triage queue of pending applications, each with a document pipeline to approve or reject.',
      points: [
        { title: 'Work the queue', desc: 'Applications list with their stage; open one to review its submitted documents.' },
        { title: 'Follow the pipeline', desc: 'A staged progress view shows where each application sits and what’s left before a decision.' },
        { title: 'Decide', desc: 'Approve to release the agency into live trading (auto-generating its master agreement), or reject with a reason.' },
      ],
    },
    related: ['agency-onboarding', 'contract-templates', 'agencies'],
  },

  'agency-profile': {
    title: 'Agency profile',
    tag: 'Record',
    tagline: 'A single agency’s full commercial and compliance relationship.',
    business: {
      lead: 'The profile is the complete picture of one trading relationship — its standing, its money, and the terms it operates under. It’s what you open to understand or adjust how a specific agency does business with the platform.',
      points: [
        { title: 'Terms live here', desc: 'Tier, markup inheritance and product access define this agency’s economics and are managed on this record.' },
        { title: 'Standing at a glance', desc: 'Wallet, credit and KYC status tell you instantly whether the agency can trade right now.' },
        { title: 'Inherit or override', desc: 'An agency can follow its tier’s defaults, or carry bespoke overrides negotiated just for it.' },
      ],
    },
    software: {
      lead: 'A per-agency detail surface pulling identity, money and configuration into one view.',
      points: [
        { title: 'Manage assets & access', desc: 'SIGNs, sale channels, branches, roles and product access for this agency are reachable from here.' },
        { title: 'Group memberships', desc: 'The agency’s pricing group memberships show as a chip strip and feed Group-scoped rules.' },
        { title: 'Self vs admin view', desc: 'An agency sees its own profile read-mostly; an admin can edit terms and take action.' },
      ],
    },
    related: ['agencies', 'markup', 'branches', 'signs'],
  },

  'contract-templates': {
    title: 'Contract templates',
    tag: 'Agreements',
    tagline: 'The terms templates the master agreement is generated from.',
    business: {
      lead: 'Every agency trades under a master agreement. Contract templates are the versioned terms those agreements are generated from — so the legal relationship is consistent, current, and auditable across the whole network.',
      points: [
        { title: 'One source of terms', desc: 'Generating an agency’s contract pulls from this catalogue, so everyone signs the same, current terms.' },
        { title: 'Versioned & auditable', desc: 'New versions supersede old ones; retired versions stay available for renewing older contracts.' },
        { title: 'Feeds the lifecycle', desc: 'A template is the input to the generate → send → sign → counter-sign → active agreement flow.' },
      ],
    },
    software: {
      lead: 'The catalogue of template versions the generate form reads.',
      points: [
        { title: 'Add a version', desc: 'Create a new template version and upload its source document.' },
        { title: 'Retire, don’t delete', desc: 'Retire superseded versions; they stay resolvable for contracts already on them.' },
        { title: 'Picker source', desc: 'The contract generate form’s template picker reads this catalogue.' },
      ],
    },
    related: ['agency-onboarding', 'agency-kyc', 'agencies'],
  },

  // ── SYSTEM section ──────────────────────────────────────────────────────

  'reference-data': {
    title: 'Reference Data',
    tag: 'Master vocabularies',
    tagline: 'The canonical lookup lists every booking, voucher and price is built from.',
    business: {
      lead: 'Reference data is the shared source of truth — airlines, airports, cities, currencies, room types, cabin classes and dozens more. Normalising these once means search results, tickets, vouchers and pricing rules all speak the same language across suppliers and services.',
      points: [
        { title: 'Consistency across the platform', desc: 'One curated list feeds every service, so a supplier’s codes map to the same canonical entry everywhere.' },
        { title: 'Change once, apply everywhere', desc: 'Editing a reference row updates it for search, booking, documents and reporting at once.' },
        { title: 'Governed, not free-text', desc: 'Controlled vocabularies keep dirty data out of pricing and channel mapping.' },
      ],
    },
    software: {
      lead: 'A schema-driven admin surface over ~35 reference categories grouped by domain.',
      points: [
        { title: 'Browse & edit by category', desc: 'Pick a category, search its rows, and add / edit / activate-deactivate entries; some are effective-dated (currencies, taxes, FX).' },
        { title: 'Fine-grained access', desc: 'View / Add / Edit / Status are granted per category via Roles & Access — the buttons here reflect what your role allows.' },
        { title: 'Canonical vs editable', desc: 'A few lists (countries, timezones) are canonical and managed centrally, not created ad-hoc.' },
      ],
    },
    related: ['rbac', 'data-simulator'],
  },

  'data-simulator': {
    title: 'Data Simulator',
    tag: 'Synthetic data',
    tagline: 'Generate realistic bookings to exercise the platform without real orders.',
    business: {
      lead: 'The simulator manufactures believable demand — bookings across products, agencies and dates — so dashboards, reports, pricing and settlement can be demonstrated and stress-tested before, or alongside, real trading.',
      points: [
        { title: 'Prove the platform', desc: 'A populated system shows stakeholders how reporting and money flows behave at volume.' },
        { title: 'Safe to undo', desc: 'Generated orders are tagged so an entire batch can be rolled back cleanly.' },
        { title: 'Targeted scenarios', desc: 'Aim a batch at specific agencies or products to model a particular slice of business.' },
      ],
    },
    software: {
      lead: 'A batch generator that injects real, tagged orders into the booking engine.',
      points: [
        { title: 'Configure a batch', desc: 'Choose volume, product mix, agencies and date range, then publish.' },
        { title: 'Real orders, flagged', desc: 'Published items are genuine booking-service orders tagged “simulated”, so they appear in All Bookings.' },
        { title: 'Full-batch rollback', desc: 'Remove a batch in one action to return the system to its prior state.' },
      ],
    },
    lifecycles: {
      lead: 'Every order rides the same backbone — created, paid, then fulfilled — but the "fulfilled" state is named for what the product delivers. Transport issues a travel document, so its success state is «Ticketed». Hotels and most services secure a reservation with no ticket, so theirs is «Confirmed». A visa is decided rather than ticketed or confirmed, so its success state is «Approved». All three are the same milestone: the sale is real and an accounting sale voucher posts. From there a booking can be cancelled and refunded, and unhappy paths (payment failure, an expired hold, an operator rejection, a visa refusal) end in their own terminal states.',
      legend: [
        { label: 'In progress', kind: 'neutral' },
        { label: 'Awaiting an action', kind: 'warn' },
        { label: 'Sale is real → posts a voucher', kind: 'success' },
        { label: 'Unhappy / terminal', kind: 'danger' },
      ],
      items: [
        {
          product: 'Flights, Trains & Buses',
          success: 'Ticketed',
          lead: 'Transport holds inventory as a PNR, then issues a numbered ticket. «Ticketed» is the point of no easy return — the document is emitted and the sale posts.',
          path: [
            { status: 'Draft', kind: 'neutral', note: 'cart built' },
            { status: 'Held', kind: 'warn', note: 'PNR holds a seat' },
            { status: 'Pending payment', kind: 'neutral' },
            { status: 'Ticketed', kind: 'success', note: 'e-ticket / PNR issued · voucher posts' },
          ],
          branches: [
            { from: 'Held', nodes: [{ status: 'Expired', kind: 'danger' }], desc: 'The hold window lapses (a sweeper runs every ~60s) and the seat is auto-released back to the supplier.' },
            { from: 'Pending payment', nodes: [{ status: 'Failed', kind: 'danger' }], desc: 'Payment is declined or the supplier fare expires on the retry — nothing is ticketed.' },
            { from: 'Ticketed', nodes: [{ status: 'Cancelled', kind: 'danger' }, { status: 'Refunded', kind: 'neutral' }], desc: 'Post-sale: a cancellation quote runs, then any captured money is returned less penalty.' },
          ],
        },
        {
          product: 'Hotels',
          success: 'Confirmed',
          lead: 'A hotel is never ticketed — it returns a confirmation, not a document. Instant properties confirm on payment; On-Request properties authorise the card first and wait for the operator (In review) before capture.',
          path: [
            { status: 'Draft', kind: 'neutral', note: 'cart built' },
            { status: 'Pending payment', kind: 'neutral', note: 'Instant: pay to confirm' },
            { status: 'Confirmed', kind: 'success', note: 'reservation secured · voucher posts' },
          ],
          branches: [
            { from: 'Pending payment', label: 'On-Request', nodes: [{ status: 'In review', kind: 'warn', note: 'operator decides' }, { status: 'Confirmed', kind: 'success' }], desc: 'The card is authorised, the request sits with the operator; on confirm the payment is captured and the stay is Confirmed.' },
            { from: 'In review', nodes: [{ status: 'Rejected', kind: 'danger' }], desc: 'The operator declines — the authorisation is voided, so no money is captured.' },
            { from: 'Confirmed', nodes: [{ status: 'Cancelled', kind: 'danger' }, { status: 'Refunded', kind: 'neutral' }], desc: 'Post-sale cancellation follows the rate’s rules; a refund returns captured money less penalty.' },
          ],
        },
        {
          product: 'Tours & other direct services (CIP, Transfer, SIM, Insurance)',
          success: 'Confirmed',
          lead: 'Direct-service products issue a voucher/confirmation, not a ticket, so they complete at «Confirmed». A tour bundles a flight + hotel but is sold at one supplier-independent sale price — one order, one voucher. (CIP, Transfer, SIM and Insurance are modelled but not yet live.)',
          path: [
            { status: 'Draft', kind: 'neutral', note: 'cart built' },
            { status: 'Pending payment', kind: 'neutral' },
            { status: 'Confirmed', kind: 'success', note: 'service secured · voucher posts' },
          ],
          branches: [
            { from: 'Pending payment', nodes: [{ status: 'Failed', kind: 'danger' }], desc: 'Payment is declined — the service is not confirmed.' },
            { from: 'Confirmed', nodes: [{ status: 'Cancelled', kind: 'danger' }, { status: 'Refunded', kind: 'neutral' }], desc: 'Post-sale cancellation and refund, subject to the product’s cancellation rules.' },
          ],
        },
        {
          product: 'Visa',
          success: 'Approved',
          lead: 'A visa is decided, not ticketed or confirmed: the application is submitted, sits under review, then is either approved or refused. «Approved» is visa’s success state — the same "sale is real" milestone as Confirmed, and it posts the sale voucher. It reduces to three states: In review → Approved / Rejected.',
          path: [
            { status: 'Draft', kind: 'neutral', note: 'application built' },
            { status: 'Pending payment', kind: 'neutral' },
            { status: 'In review', kind: 'warn', note: 'under processing' },
            { status: 'Approved', kind: 'success', note: 'visa granted · voucher posts' },
          ],
          branches: [
            { from: 'In review', nodes: [{ status: 'Rejected', kind: 'danger' }], desc: 'The application is refused — no voucher. A payment failure or a cancellation on a visa also collapses to Rejected.' },
          ],
        },
      ],
    },
    related: ['reference-data', 'rbac'],
  },

  rbac: {
    title: 'Roles & Access',
    tag: 'RBAC',
    tagline: 'Compose fine-grained permissions into the roles your people hold.',
    business: {
      lead: 'Access is governed by permissions, not job titles. Roles & Access is where you assemble atomic permission codes into roles and grant them — so each person can do exactly what they should and nothing more, whether they’re platform staff or an agency user.',
      points: [
        { title: 'Least privilege', desc: 'Granting narrow permissions limits blast radius if an account is misused.' },
        { title: 'Two scopes', desc: 'Platform roles act across the network; agency roles are confined to their own tenant.' },
        { title: 'Lockout-safe', desc: 'Sealed system roles (e.g. Platform Admin) keep their permissions locked so no one can strip their own access.' },
      ],
    },
    software: {
      lead: 'Roles, Users and a live permission catalogue in one place.',
      points: [
        { title: 'Build a role', desc: 'Tick permission codes grouped by module; a role is just the set you select.' },
        { title: 'Reference-data matrix', desc: 'Reference-data permissions are granted per category × action (View/Add/Edit/Status); agency roles get View only.' },
        { title: 'Assign to users', desc: 'The Users tab attaches roles to people, scoped to platform or a specific agency.' },
      ],
    },
    related: ['users', 'reference-data', 'settings'],
  },

  users: {
    title: 'Users',
    tag: 'Accounts',
    tagline: 'The people who can sign in, and the roles they carry.',
    business: {
      lead: 'Users are the individuals operating the platform — platform staff and agency staff. Managing them here is how you control who has access, keep it current as people join and leave, and tie every action to an accountable account.',
      points: [
        { title: 'Access follows employment', desc: 'Provisioning and deactivating accounts promptly is the front line of security.' },
        { title: 'Roles carry the power', desc: 'A user does nothing on their own — their roles decide what they can do.' },
        { title: 'Accountability', desc: 'Actions are attributable to a named account, which matters for audit and disputes.' },
      ],
    },
    software: {
      lead: 'The directory of accounts with their roles and status.',
      points: [
        { title: 'Provision & assign', desc: 'Create a user and assign one or more roles, scoped to platform or an agency.' },
        { title: 'Activate / deactivate', desc: 'Disable access without deleting history when someone leaves.' },
        { title: 'Scope-aware', desc: 'Admins manage all users; an agency admin manages only their own people.' },
      ],
    },
    related: ['rbac', 'settings'],
  },

  settings: {
    title: 'Settings',
    tag: 'Configuration',
    tagline: 'Platform- and agency-level configuration in one place.',
    business: {
      lead: 'Settings hold the knobs that shape how the platform presents and behaves — branding, notifications, integrations and default configuration. Getting them right keeps the experience consistent and the plumbing connected.',
      points: [
        { title: 'Identity & experience', desc: 'Branding and defaults set how the console looks and behaves for your users.' },
        { title: 'Stay connected', desc: 'Integration and notification settings keep the platform talking to the outside world.' },
        { title: 'Scoped ownership', desc: 'Platform admins manage platform-wide settings; agency admins manage their own scope.' },
      ],
    },
    software: {
      lead: 'Grouped configuration panels for branding, notifications, integrations and agency defaults.',
      points: [
        { title: 'Edit by section', desc: 'Work through the grouped panels and save the areas you manage.' },
        { title: 'Permission-gated', desc: 'Access requires the settings-manage permission; the sidebar entry stays consistent with the rest of System.' },
        { title: 'Platform vs agency', desc: 'What you can change depends on whether you hold platform or agency scope.' },
      ],
    },
    related: ['rbac', 'users'],
  },

  issues: {
    title: 'Issue Tracker',
    tag: 'Internal QA',
    tagline: 'The internal defect tracker for reporting and triaging product issues.',
    business: {
      lead: 'The Issue Tracker is how problems found in the console get captured, triaged and fixed — a private engineering feedback loop separate from customer support tickets, so quality improves without noise reaching agencies.',
      points: [
        { title: 'Close the loop', desc: 'A structured report with context turns “it’s broken” into something an engineer can act on.' },
        { title: 'Not customer support', desc: 'This is internal QA — distinct from the agency-facing Support Tickets.' },
        { title: 'Evidence attached', desc: 'Screenshots and environment context make issues reproducible.' },
      ],
    },
    software: {
      lead: 'A triage queue of reported issues with snapshots and status.',
      points: [
        { title: 'Report anywhere', desc: 'Capture an issue (with a snapshot) from anywhere via the reporter shortcut, then track it here.' },
        { title: 'Triage & status', desc: 'Work the queue — assign, prioritise and move issues through their lifecycle.' },
        { title: 'Developer-scoped', desc: 'Full triage is gated to the Developer role, so the Tracker doesn’t clutter every admin’s view.' },
      ],
    },
    related: ['my-issues'],
  },

  'my-issues': {
    title: 'My Reported Issues',
    tag: 'My reports',
    tagline: 'The issues you personally raised, and where each one stands.',
    business: {
      lead: 'This is your own slice of the Issue Tracker — the reports you filed and their progress. It closes the feedback loop for the reporter: you can see that what you raised was seen and is moving.',
      points: [
        { title: 'Visibility for reporters', desc: 'Knowing a report was received and is progressing encourages people to keep flagging problems.' },
        { title: 'Your context', desc: 'Only the issues you raised, so the list stays personally relevant.' },
        { title: 'Follow to resolution', desc: 'Track each report from open to resolved.' },
      ],
    },
    software: {
      lead: 'A filtered view of the Issue Tracker scoped to your own reports.',
      points: [
        { title: 'See your reports', desc: 'Every issue you filed, with its current status.' },
        { title: 'Open for detail', desc: 'Drill into a report to see its thread, snapshot and updates.' },
        { title: 'Raise more', desc: 'File new issues from the reporter shortcut; they appear here.' },
      ],
    },
    related: ['issues'],
  },

  // ── SUPPLY section ──────────────────────────────────────────────────────

  'supply-properties': {
    title: 'Hotel properties',
    tag: 'Own inventory',
    tagline: 'Your directory of directly-contracted hotels — the rooms you own and sell yourself.',
    business: {
      lead: 'These are hotels you contract directly, as opposed to the fares you resell from external hotel engines. Because the inventory is yours, you set the rooms, the nightly rates and the availability — and you keep the whole margin instead of a bedbank’s.',
      points: [
        { title: 'Own vs resold inventory', desc: 'Own properties sit alongside external hotel-engine results in Search & Book; here you control price and availability end to end.' },
        { title: 'Scope decides reach', desc: 'A platform-scoped property becomes shared inventory any agency can sell; an agency-scoped one stays private to that agency.' },
        { title: 'Sell mode at a glance', desc: 'Each property is Instant (confirms immediately) or On-Request (a booking the operator confirms) — the card flags which.' },
      ],
    },
    software: {
      lead: 'A searchable, filterable grid of your properties with quick status.',
      points: [
        { title: 'Find a property', desc: 'Search by name or city and filter by scope, sell mode or status.' },
        { title: 'Onboard a new one', desc: 'Create from scratch, or seed from a reference hotel, via the onboarding drawer.' },
        { title: 'Open to manage', desc: 'Click through to the property record to manage its rooms, rates, allotment, ancillaries and contract.' },
      ],
    },
    related: ['supply-property-detail', 'reference-data'],
  },

  'supply-property-detail': {
    title: 'Hotel Property',
    tag: 'Property record',
    tagline: 'One property’s full record — rooms, rates, availability, extras and the contract behind it.',
    business: {
      lead: 'A hotel property is a piece of inventory you own and sell directly. Everything a booking needs lives here: what rooms exist, what they cost each night, how many are available, what extras can be added, and the contract that governs it. Get this right and the property is ready to sell across both your B2B console and B2C.',
      points: [
        { title: 'Own inventory, your margin', desc: 'A direct contract you load yourself carries no bedbank markup — the spread between net and sell price is entirely yours.' },
        { title: 'Platform vs agency scope', desc: 'Platform scope publishes the property as shared inventory to agencies; agency scope keeps it private. Platform-owned rows are read-only to an agency.' },
        { title: 'Instant vs On-Request', desc: 'Instant confirms immediately against allotment; On-Request captures the booking as a request the operator confirms or rejects within an SLA (payment is authorised, captured on confirm).' },
        { title: 'Availability protects you', desc: 'Allotment is the contracted room count; holds and sold rooms are deducted so an Instant sale can never oversell.' },
      ],
    },
    software: {
      lead: 'A tabbed workspace for one property — each tab is a section of the record, with a badge showing how much it holds. Below is what every tab does and how its items work.',
      points: [
        { title: 'Overview', desc: 'The read-first summary: location & map, star rating, chain, headline amenities, description, and live KPIs — occupancy MTD, ADR and RevPAR (all computed against the property’s total room inventory).' },
        { title: 'Rooms & room types', desc: 'Add / edit each room type: pick a standardised category from the Room types reference (this is what drives search matching & channel mapping), a guest-facing display name and short code, max occupancy, size in m², and total physical inventory — the last is the divisor behind Overview’s occupancy / RevPAR.' },
        { title: 'Beds (per room type)', desc: 'The bed setup is a composer, not free text: rows of count × bed type drawn from the Bed types reference (e.g. “1 King + 1 Sofa bed”). The composed string is what shows in search results and on the voucher, so it stays consistent everywhere.' },
        { title: 'Amenities (per room & property)', desc: 'A reference-backed multi-select grouped by Amenity category (managed in Reference data → Amenities). Pick from the controlled list rather than typing free text; selections are stored as display labels so they render identically in the B2B console, on the voucher and in B2C.' },
        { title: 'Gallery', desc: 'Upload and order property- and room-level photos. These are the images agents see in Search & Book and guests see on the voucher; a room with no photo falls back to the property gallery.' },
        { title: 'Rate plans', desc: 'A rate plan is a sellable package — Room Only, Bed & Breakfast, Non-refundable, etc. Each carries its own prices and can override the property’s sell mode (Instant / On-Request). The calendar and search prices are always per rate plan.' },
        { title: 'Rates & calendar', desc: 'Per rate plan, set the net price for a single night, a date range, or a whole season in one action. Weekend and season dates carry visual cues (a coloured pill), and stop-sell closes a date without deleting its price. A rate value is required to save — an empty set is rejected.' },
        { title: 'Allotment & holds', desc: 'The contracted number of rooms available per day. The grid breaks each day into held (booked, payment not yet captured) vs sold (confirmed) vs free-to-sell; bulk-update a count across a date range. Instant sales decrement it in real time; On-Request sales don’t touch it until the operator confirms.' },
        { title: 'Ancillary services', desc: 'The extras this property sells — airport transfer, breakfast, spa, late checkout — each sourced from the Ancillary service types reference with a price and a charge basis (per booking / per night / per pax). They surface as add-ons on a booking’s Charges.' },
        { title: 'Live bookings', desc: 'Every booking made against this property. For On-Request stays the Confirm / Reject bar lives here: Confirm captures the authorised payment and commits allotment; Reject voids the authorisation — both within the property’s SLA window.' },
        { title: 'Contract & settings', desc: 'The supplier contract plus the operational switches: sell mode (Instant, or On-Request with its SLA hours and booking-cutoff days), platform vs agency scope, and the property’s active status.' },
        { title: 'Onboard, edit & bulk tools', desc: 'A guided onboarding drawer creates a property from scratch or seeded from a reference hotel; the edit drawer adjusts core details later; and the bulk drawers set rates or allotment across many days at once.' },
      ],
    },
    modes: {
      lead: 'Two switches change how a property behaves — sell mode (set under Contract & settings, overridable per rate plan) and scope (set when the property is created).',
      modes: [
        { name: 'Instant', when: 'live allotment you trust', desc: 'The booking confirms immediately and decrements allotment; payment is captured at once. It’s blocked when no room is free for the dates and occupancy.' },
        { name: 'On-Request', when: 'the hotel must confirm', desc: 'The booking is taken as a request — payment is authorised (not captured) and allotment is untouched — and the operator confirms or rejects within the SLA. Confirm captures the payment and commits allotment; reject voids the authorisation.' },
        { name: 'Platform scope', when: 'shared inventory', desc: 'The property is published to agencies as sellable inventory; they see it read-only and resell it under their own retail markup.' },
        { name: 'Agency scope', when: 'private inventory', desc: 'The property belongs to a single agency and is only sellable by it.' },
      ],
    },
    examples: [
      {
        title: 'Availability on a night (Instant sell mode)',
        rows: [
          { label: 'Contracted allotment', value: '10' },
          { label: '− On hold (awaiting capture)', value: '3', accent: 'info' },
          { label: '− Sold (confirmed)', value: '5' },
          { label: 'Free to sell', value: '2', strong: true, accent: 'accent' },
        ],
        note: 'On-Request bookings don’t consume allotment until the operator confirms — they’re gated on room type, not the daily count.',
      },
      {
        title: 'Rates & calendar — a 3-night stay (Deluxe · Bed & Breakfast)',
        rows: [
          { label: 'Thu 2 Apr · weekday net', value: '2,200,000' },
          { label: 'Fri 3 Apr · weekend net', value: '2,600,000', accent: 'info' },
          { label: 'Sat 4 Apr · weekend net', value: '2,600,000', accent: 'info' },
          { label: 'Room subtotal (3 nights)', value: '7,400,000', strong: true },
        ],
        note: 'Each night carries the net price set for its rate plan; weekend/season cues are visual aids — the price is whatever you set for that date. A stop-sell night would be unbookable regardless of price.',
      },
      {
        title: 'Ancillary services on that booking (2 guests, 3 nights)',
        rows: [
          { label: 'Room subtotal', value: '7,400,000' },
          { label: '+ Breakfast · per pax / night · 150,000 × 2 × 3', value: '+900,000', accent: 'success' },
          { label: '+ Airport transfer · per booking', value: '+1,200,000', accent: 'success' },
          { label: 'Property total (before markup)', value: '9,500,000', strong: true, accent: 'accent' },
        ],
        note: 'Each ancillary’s charge basis (per booking / per night / per pax) decides how it multiplies. Retail markup from Markup & Rules is then applied on top to reach the traveler price.',
      },
    ],
    tabs: {
      overview: {
        title: 'Overview',
        lead: 'The read-first snapshot of the property — where it is, what it offers, and how it’s performing right now.',
        points: [
          { title: 'Identity & location', desc: 'Name, star rating, chain, address and map pin — the facts a booker checks first.' },
          { title: 'Headline amenities & description', desc: 'The property’s selling points, pulled from the reference-backed amenities and the free-text description.' },
          { title: 'Live KPIs', desc: 'Occupancy MTD, ADR (average daily rate) and RevPAR (revenue per available room) — all computed against the property’s total room inventory, so they only read true once every room type has its inventory count set.' },
        ],
        examples: [
          {
            title: 'How the KPIs are computed — 40 rooms, a 30-day month',
            rows: [
              { label: 'Available room-nights (40 × 30)', value: '1,200' },
              { label: 'Sold room-nights', value: '900' },
              { label: 'Occupancy MTD (900 ÷ 1,200)', value: '75%', accent: 'info', strong: true },
              { label: 'Room revenue', value: '90,000,000' },
              { label: 'ADR (revenue ÷ sold 900)', value: '100,000' },
              { label: 'RevPAR (revenue ÷ available 1,200)', value: '75,000', accent: 'success', strong: true },
            ],
            note: 'RevPAR blends price and occupancy, so it drops if either the rate or the fill rate slips. Missing a room type’s inventory count breaks the divisor — the tiles show an em-dash until it’s set on the Rooms tab.',
          },
        ],
      },
      rooms: {
        title: 'Rooms & room types',
        lead: 'Define what the property can sell: each room type, how it sleeps, and how much of it exists.',
        points: [
          { title: 'Normalised category', desc: 'Pick a standardised type from the Room types reference — this is what drives search matching and channel mapping, not the free-text name.' },
          { title: 'Display name & code', desc: 'A guest-facing name (“Deluxe Sea View”) and a short code for the operator.' },
          { title: 'Beds — a composer, not free text', desc: 'Build the bed setup from count × Bed type rows (e.g. “1 King + 1 Sofa bed”) drawn from the Bed types reference; the composed string is what shows in search and on the voucher.' },
          { title: 'Amenities', desc: 'A reference-backed multi-select grouped by category — stored as display labels so they render identically in B2B, on the voucher and in B2C.' },
          { title: 'Total inventory', desc: 'The physical room count of this type — the divisor behind Overview’s occupancy and RevPAR, so it must be set for the KPIs to read true.' },
        ],
      },
      gallery: {
        title: 'Gallery',
        lead: 'The photos agents see in Search & Book and guests see on the voucher.',
        points: [
          { title: 'Property & room photos', desc: 'Upload at the property level and per room type; order them so the strongest image leads.' },
          { title: 'Sensible fallback', desc: 'A room type with no photo of its own falls back to the property gallery, so a listing is never image-less.' },
        ],
      },
      rates: {
        title: 'Rates & calendar',
        lead: 'Set the net price for each rate plan, one night or a whole season at a time.',
        points: [
          { title: 'A rate plan is a package', desc: 'Room Only, Bed & Breakfast, Non-refundable… each carries its own prices and can override the property’s sell mode. Search prices are always per rate plan.' },
          { title: 'Single day · range · season', desc: 'Price one night, a date range, or a whole season in one action. Weekend and season dates carry a coloured pill so the pattern is visible.' },
          { title: 'Stop-sell, don’t delete', desc: 'Closing a date with stop-sell keeps its price for later — a rate value is required to save, so an empty set is rejected.' },
        ],
        examples: [
          {
            title: 'From net rate to the price a partner sees',
            rows: [
              { label: 'Net rate you load for the night', value: '1,200,000' },
              { label: '+ Retail markup 8% (Markup & Rules)', value: '+96,000', accent: 'success' },
              { label: 'Search / sell price', value: '1,296,000', strong: true },
            ],
            note: 'You set the net here; the sell price is the net plus whatever the Retail pricing layer adds. The spread is your margin on owned inventory.',
          },
        ],
      },
      allotment: {
        title: 'Allotment & holds',
        lead: 'The contracted number of rooms available per day, and how each is spoken for.',
        points: [
          { title: 'Four states per day', desc: 'Allotment is the contracted count; each day breaks into held (booked, payment not yet captured), sold (confirmed) and free-to-sell.' },
          { title: 'Bulk set', desc: 'Set an allotment count across a whole date range in one action, the same way rates are set.' },
          { title: 'Instant vs On-Request', desc: 'Instant sales decrement free-to-sell in real time so you can never oversell; On-Request sales don’t touch allotment until the operator confirms.' },
          { title: 'Reading the Inventory allotment table', desc: 'Each row is one room type — select a row to drive the calendar below. Availability in search now honours the day-by-day calendar (rate + free-to-sell + stop-sell), the room’s Hard limit / day, its Stop-sell default, and any min-stay on the rate — plus the property’s On-Request booking cutoff. Auto-release and Group hold are saved but not yet enforced.' },
          { title: 'Column · Room type', desc: 'Which room these settings apply to. Clicking the row (or its checkbox) makes it the room shown in the calendar below.' },
          { title: 'Column · Total inventory', desc: 'The room type’s Total units — the count of physical rooms of that type. Read-only here: it mirrors what you set in the Rooms & room types tab (edit the room to change it) and shows “—” until it’s set. It is NOT set by the calendar; use it as a reference when sizing the daily allotment.' },
          { title: 'Column · Hard limit / day', desc: 'An absolute ceiling on how many rooms of this type may sell on any single day — a safety cap above the calendar allotment. Enforced in availability search: a day’s free-to-sell is capped to this. Blank = no cap.' },
          { title: 'Column · Auto-release after', desc: 'The intended point at which unsold allotment is released before check-in (72h, 7, 14 or 30 days; “Never” keeps it until departure). Editable and saved; the automatic release isn’t wired up yet.' },
          { title: 'Column · Group hold', desc: 'The intended maximum rooms of this type that may be held at once for group bookings. Editable and saved; enforcement is pending. Blank = no group-specific cap.' },
          { title: 'Column · Stop-sell', desc: 'The room-type default: when on, days with no explicit allotment row are treated as closed in availability search. A per-day stop-sell on the calendar always overrides it for that date.' },
        ],
        examples: [
          {
            title: 'A single day’s availability',
            rows: [
              { label: 'Contracted allotment', value: '20' },
              { label: '− Held (awaiting capture)', value: '−3' },
              { label: '− Sold (confirmed)', value: '−5' },
              { label: 'Free to sell', value: '12', accent: 'success', strong: true },
            ],
            note: 'An Instant booking moves one room from free → sold immediately; an On-Request booking leaves all four numbers untouched until the operator confirms it on the Live bookings tab.',
          },
        ],
      },
      ancillaries: {
        title: 'Ancillary services',
        lead: 'The extras this property sells alongside the room — transfers, breakfast, spa, late checkout.',
        points: [
          { title: 'From the reference', desc: 'Each extra’s category comes from the Ancillary service types reference; you set a price and a charge basis.' },
          { title: 'Charge basis multiplies', desc: 'Per booking (once), per night (× nights), or per pax (× guests) — the basis decides how the price scales on a booking.' },
          { title: 'Surfaces on Charges', desc: 'Added extras appear on the booking’s Charges tab and roll into the traveler price.' },
        ],
        examples: [
          {
            title: 'Extras on a 3-night, 2-guest stay',
            rows: [
              { label: 'Airport transfer · 450,000 per booking', value: '450,000' },
              { label: 'Breakfast · 250,000 per night × 3', value: '750,000' },
              { label: 'Total extras added to the booking', value: '1,200,000', accent: 'accent', strong: true },
            ],
            note: 'The charge basis is what makes the same list price scale differently — per-booking is charged once, per-night follows the length of stay, per-pax follows the headcount.',
          },
        ],
      },
      bookings: {
        title: 'Live bookings',
        lead: 'Every booking made against this property — and where On-Request stays are confirmed.',
        points: [
          { title: 'The full booking list', desc: 'See who booked, which room and rate plan, the dates and the status.' },
          { title: 'Confirm / Reject On-Request', desc: 'For On-Request stays the decision bar lives here: Confirm captures the authorised payment and commits allotment; Reject voids the authorisation.' },
          { title: 'Inside the SLA', desc: 'Both actions must happen within the property’s On-Request SLA window (set under Contract & settings), or the request lapses.' },
        ],
      },
      settings: {
        title: 'Contract & settings',
        lead: 'The supplier contract plus the operational switches that govern how the property sells.',
        points: [
          { title: 'Contract terms', desc: 'The commercial agreement behind the property — the basis for the net rates you load.' },
          { title: 'Sell mode', desc: 'Instant, or On-Request with its SLA hours and booking-cutoff days. Rate plans can override this per plan.' },
          { title: 'Scope & status', desc: 'Platform (shared to agencies, read-only for them) vs Agency (private), plus the property’s active / paused status.' },
        ],
      },
    },
    related: ['supply-properties', 'reference-data', 'markup'],
  },

  // ── INVENTORY section ───────────────────────────────────────────────────
  //
  // These are the search-and-book funnels — one per product. The "business"
  // axis frames each product's commercial shape (who supplies it, who pays
  // for it, why the platform bothers offering it); the "software" axis maps
  // to the concrete search → results → offer → book flow the operator sees.
  // Copy is grounded in this platform's actual models (SIGN-scoped supply for
  // flights, On-Request vs Instant for hotels, price-owning services for
  // CIP/visa) rather than a generic OTA description.

  'flights-search': {
    title: 'Flights',
    tag: 'Flight search',
    tagline: 'The point-of-sale funnel for booking flights from every supplier the agency can ticket.',
    business: {
      lead: 'Flights is where an agency turns a customer\'s trip into a ticket. Every result on this screen is issuable — either against the agency\'s own SIGN or via a partner SIGN it has been granted access to — so the fares displayed are the ones the agency can actually sell without a further approval step.',
      points: [
        { title: 'One search across many pipes', desc: 'A single query is fanned out to every content source the agency has active (own SIGN, borrowed SIGNs via Partner Access, NDC/GDS feeds) and the results are merged into one price-ranked list.' },
        { title: 'Layered pricing is applied here', desc: 'Displayed prices already include the Wholesale (SIGN owner\'s), Distribution (channel) and Retail (own) markups from the pricing engine — what the customer sees is what the agency plans to charge.' },
        { title: 'Traveler-first, not fare-first', desc: 'Passenger types (adult / child / infant) and travel dates drive both the price and downstream refund / rebook eligibility, so the input form matters.' },
      ],
    },
    software: {
      lead: 'A fare-search screen wired to a real availability API, with results grouped by price and issuing SIGN.',
      points: [
        { title: 'Compose the query', desc: 'Origin, destination, dates and pax counts drive the search; class / airline / cabin can be narrowed after the first result set.' },
        { title: 'Read the results row', desc: 'Each row shows the fare, the airline, the cabin, and which SIGN the ticket will be issued against — the badge on the right.' },
        { title: 'Progress to book', desc: 'Selecting a fare opens the offer detail; from there the passenger-form / seat-map / payment flow completes the reservation into a booking record.' },
      ],
    },
    flow: {
      lead: 'The end-to-end path from a customer request to an issued ticket.',
      steps: [
        { title: 'Enter the trip', desc: 'Origin, destination, one-way or return, travel dates, and adult / child / infant counts.' },
        { title: 'Run the search', desc: 'Results are fanned out to every active supplier and merged into one price-ranked list.' },
        { title: 'Filter and rank', desc: 'Narrow by airline, cabin, stops, and departure time; the issuing SIGN is visible on every row.' },
        { title: 'Open the offer', desc: 'The offer detail shows the fare rules, baggage, refund and rebook policy — read them before committing.' },
        { title: 'Register passengers', desc: 'Fill the passenger form (name, DOB, document, frequent-flyer) for each traveler; the CRM picker prefills from saved customers.' },
        { title: 'Choose seats and ancillaries', desc: 'Seat-map picker and paid ancillaries (baggage, meal) are optional add-ons on the offer.' },
        { title: 'Pay and issue', desc: 'Payment is captured against the agency\'s wallet / credit line; the PNR is booked and the e-ticket is issued.' },
      ],
    },
    modes: {
      lead: 'A flight offer is fulfilled through one of three supply modes — the badge on each result tells you which one you\'re getting.',
      modes: [
        { name: 'Own SIGN', when: 'agency has direct BSP / GDS credentials', desc: 'The agency ticket is issued against its own SIGN. Full commission share, fastest issuance, tightest support loop with the airline.' },
        { name: 'Partner SIGN', when: 'agency uses another agency\'s grant', desc: 'Ticketed against a SIGN that was granted via Partner Access. Wholesale layer of the pricing engine takes the SIGN owner\'s markup.' },
        { name: 'NDC / connected', when: 'no BSP relationship for that airline', desc: 'Fare comes from an NDC or aggregator feed. Rules may differ from GDS content (refunds handled by the connector, seat map may be limited).' },
      ],
    },
    examples: [
      {
        title: 'A DXB → LHR business round-trip · 2 adults · own SIGN',
        rows: [
          { label: 'Base fare (per pax) × 2', value: '28,000,000' },
          { label: 'Taxes & YQ', value: '4,200,000' },
          { label: 'Wholesale MARKUP · not applicable (own SIGN)', value: '0' },
          { label: 'Retail MARKUP · +5% of gross', value: '+1,610,000', accent: 'success' },
          { label: 'Traveler total', value: '33,810,000', strong: true },
        ],
        note: 'Own-SIGN issuance skips the Wholesale layer entirely — the SIGN owner IS the seller, so there\'s no reseller margin to pay.',
      },
      {
        title: 'Same trip · borrowed partner SIGN',
        rows: [
          { label: 'Base fare + taxes', value: '32,200,000' },
          { label: 'Wholesale MARKUP · +4% (partner\'s cut)', value: '+1,288,000', accent: 'info' },
          { label: 'Retail MARKUP · +5% of gross', value: '+1,674,400', accent: 'success' },
          { label: 'Traveler total', value: '35,162,400', strong: true },
        ],
        note: 'The Wholesale layer pays the partner agency for the SIGN grant. Retail is then computed off the new running total.',
      },
    ],
    related: ['hotels-search', 'markup', 'signs', 'partner-access'],
  },

  'hotels-search': {
    title: 'Hotels',
    tag: 'Hotel search',
    tagline: 'Search-and-book flow for hotel stays across own inventory and connected suppliers.',
    business: {
      lead: 'Hotels lets an agency sell a stay whether the room comes from the agency\'s own contracted inventory, from a partner\'s inventory shared via a grant, or from a connected external supplier. Two sell modes coexist: Instant (the operator confirms up-front) and On-Request (the operator holds the request until a supplier confirms), and the screen makes clear which mode applies to each result.',
      points: [
        { title: 'Own vs connected supply', desc: 'Own-contracted rooms live in the platform\'s Hotel Property module; connected supply comes from external providers. The engine merges both into one result set.' },
        { title: 'Instant or On-Request', desc: 'Instant results confirm at booking time; On-Request results are quoted and held until the operator (or supplier) accepts them — the workflow diverges from that decision.' },
        { title: 'Nightly pricing = layered pricing', desc: 'The nightly rate shown already includes the pricing engine\'s Wholesale / Distribution / Retail layers; blanket per-night discounts (e.g. LOS-based) can also apply.' },
      ],
    },
    software: {
      lead: 'A destination-search screen with room / rate-plan results and a book-now / request-to-book split.',
      points: [
        { title: 'Search by destination + dates', desc: 'City, check-in / check-out and rooms drive the primary search; filters (star rating, board, chain) narrow the results.' },
        { title: 'Read the mode badge', desc: 'Each property tile carries an Instant or On-Request badge so the sell mode is visible before the operator commits.' },
        { title: 'Continue to book or request', desc: 'Selecting a room advances to the guest-form and payment flow for Instant; for On-Request it opens a request form that becomes a pending booking until the operator confirms.' },
      ],
    },
    flow: {
      lead: 'From "I need a room in Dubai next week" to a confirmed reservation (or a held request).',
      steps: [
        { title: 'Enter the stay', desc: 'Destination city, check-in / check-out dates, room count and guest counts.' },
        { title: 'Run the search', desc: 'Results combine own-contracted rooms and connected supplier feeds into one price-ranked list.' },
        { title: 'Filter properties', desc: 'Narrow by star rating, board type, chain, distance-to-centre and price range; each tile shows an Instant or On-Request badge.' },
        { title: 'Open the property', desc: 'Room types, rate plans, cancellation policy, board and inclusions are all on the property detail screen.' },
        { title: 'Pick a rate plan', desc: 'Choose which rate plan to book (Room-Only, B&B, Half-Board, Non-Refundable) and the resulting nightly price.' },
        { title: 'Register guests + pay', desc: 'Guest form + payment for Instant. For On-Request, submit the request — the booking is held pending confirmation.' },
        { title: 'Get the voucher', desc: 'Once confirmed, the hotel voucher and vendor confirmation are attached to the booking record.' },
      ],
    },
    modes: {
      lead: 'The Instant / On-Request badge on every result tells the operator how the reservation will confirm and when money moves.',
      modes: [
        { name: 'Instant', when: 'own inventory or connected supplier with real-time availability', desc: 'Booking is confirmed at the click. Payment is captured immediately. The voucher issues right after. Allotment is decremented on the spot.' },
        { name: 'On-Request', when: 'external supplier without real-time availability, or off-allotment inquiry', desc: 'The reservation is held as a pending request until the operator (or supplier) accepts. Nothing is decremented from allotment until confirmation; payment is captured at confirmation, not at request time.' },
        { name: 'Own inventory', when: 'the agency has direct contracts', desc: 'Rooms are sourced from the platform\'s Hotel Property module. Allotment, rate plans and cancellation policies are managed by the agency itself.' },
        { name: 'Connected supply', when: 'external supplier feed', desc: 'Rooms come from a partner API (bedbank, aggregator). Policies + refund flow follow the supplier\'s contract, not the agency\'s.' },
      ],
    },
    examples: [
      {
        title: 'A 3-night stay · 1 room · 2 guests · Half-Board · Instant',
        rows: [
          { label: 'Nightly rate × 3 nights', value: '9,000,000' },
          { label: 'Wholesale MARKUP · +3% (supplier margin)', value: '+270,000', accent: 'info' },
          { label: 'Retail MARKUP · +6% of gross', value: '+556,200', accent: 'success' },
          { label: 'Traveler total', value: '9,826,200', strong: true },
        ],
        note: 'Instant confirms immediately; allotment on the rate plan drops by 1 room-night for each of the 3 nights.',
      },
      {
        title: 'Same stay · On-Request',
        rows: [
          { label: 'Quoted total (held, not charged)', value: '9,826,200' },
          { label: 'On confirmation', value: 'payment captured', accent: 'success' },
          { label: 'On rejection', value: 'nothing charged', accent: 'info' },
        ],
        note: 'On-Request quotes lock the price for the SLA window. If the supplier can\'t confirm inside that window, the request auto-lapses.',
      },
    ],
    related: ['tours', 'markup', 'reference-data'],
  },

  tours: {
    title: 'Tours',
    tag: 'Tour packages',
    tagline: 'Bookable multi-service packages — hotel + flight + activities — sold as a single line.',
    business: {
      lead: 'Tours turns a bundle (hotel + flight + optional transfers, meals, activities) into one sellable product. The commercial value is packaged margin: the operator can price the bundle to a specific corridor of demand (corporate teams, pilgrimages, family holidays) without exposing the per-component fares individually.',
      points: [
        { title: 'One SKU, many components', desc: 'A tour package is a single bookable record even though it stitches together flights, rooms and services; that makes revenue attribution and cancellation cleaner than selling the components separately.' },
        { title: 'Departures and inventory', desc: 'A package is offered on specific departure dates with a fixed cap; sold-out dates fall out of the results automatically.' },
        { title: 'Same pricing engine', desc: 'Layered pricing still applies — a tour is not exempt from the Wholesale / Distribution / Retail rule stack.' },
      ],
    },
    software: {
      lead: 'A package-search screen backed by the tour catalogue, with departure-date and inclusion filters.',
      points: [
        { title: 'Find a package', desc: 'Destination, departure window and traveler counts drive the results; category and inclusion filters narrow further.' },
        { title: 'Inspect the package', desc: 'Selecting a package opens its full itinerary and inclusion list before committing to a departure date.' },
        { title: 'Book onto a departure', desc: 'Choose a date, register passengers via the tour registration drawer, and complete payment — the result is one tour booking, not one per component.' },
      ],
    },
    flow: {
      lead: 'From "we want a Georgia trip in October" to a confirmed package on a specific departure.',
      steps: [
        { title: 'Enter the trip', desc: 'Destination country / region, departure month, adult / child / infant counts.' },
        { title: 'Browse the results', desc: 'Cards show the package title, duration, headline inclusions and starting-from price for the cheapest departure.' },
        { title: 'Open the package', desc: 'The detail screen shows the day-by-day itinerary, inclusions, exclusions, hotel + flight components and cancellation policy.' },
        { title: 'Pick a departure', desc: 'The departure calendar shows available dates with per-date prices and seats-remaining; sold-out dates are dimmed.' },
        { title: 'Register travelers', desc: 'The tour registration drawer collects every passenger\'s document, meal, room-share preference and any activity opt-in.' },
        { title: 'Pay and confirm', desc: 'One payment closes the package. All component vouchers (flight, hotel, activities) attach to the single tour booking.' },
      ],
    },
    modes: {
      lead: 'Tours are catalogued along two orthogonal axes — the trip length and the departure model.',
      modes: [
        { name: 'Fixed departure', when: 'group tour with a set date and cap', desc: 'The whole group flies and stays together on the pre-set dates. Departures fill up until the cap is hit, then close.' },
        { name: 'Private / on-demand', when: 'independent traveler who wants their own dates', desc: 'The package framework (hotels, transfers, activities) is reused but the flights and itinerary shift to the traveler\'s dates.' },
        { name: 'Land-only', when: 'flight is booked elsewhere', desc: 'Hotel + transfers + activities without the flight component — priced lower and the traveler brings their own ticket.' },
        { name: 'Full package', when: 'default', desc: 'Flight + hotel + transfers + activities as one price. Highest margin per booking, simplest to sell to a first-time traveler.' },
      ],
    },
    examples: [
      {
        title: 'Georgia 5-day package · 2 adults · Full package',
        rows: [
          { label: 'Flight component (2 pax)', value: '12,000,000' },
          { label: 'Hotel component (4 nights × 2 pax)', value: '18,000,000' },
          { label: 'Transfers + activities', value: '5,000,000' },
          { label: 'Base package cost', value: '35,000,000' },
          { label: 'Wholesale MARKUP · +5%', value: '+1,750,000', accent: 'info' },
          { label: 'Retail MARKUP · +8% of gross', value: '+2,940,000', accent: 'success' },
          { label: 'Traveler total', value: '39,690,000', strong: true },
        ],
        note: 'One package cost hides the component fares from the customer — the operator markets a headline price rather than defending three separate line items.',
      },
    ],
    related: ['hotels-search', 'flights-search', 'markup'],
  },

  'trains-search': {
    title: 'Trains',
    tag: 'Rail search',
    tagline: 'Search-and-book for rail journeys across connected rail operators.',
    business: {
      lead: 'Trains sells rail as an alternative or complement to flights on domestic and short-haul corridors. Rail\'s commercial shape differs from air (station-to-station, class-based rather than cabin-based, tighter cancellation rules) and the screen makes those differences explicit so an agent quoting a trip picks the right product for the trip length.',
      points: [
        { title: 'Different pricing rhythm', desc: 'Rail fares don\'t swing minute-by-minute like flights; static timetables mean the same fare is often available for hours, so quotes are more stable.' },
        { title: 'Class matters more than airline', desc: 'The choice is between economy / first / business class on the same train, not between competing carriers — the buyer\'s decision axis is different.' },
        { title: 'Reserved vs open tickets', desc: 'Some routes require a seat reservation, others accept open tickets; the flow adapts per operator.' },
      ],
    },
    software: {
      lead: 'A station-to-station search with journey / class / operator filters.',
      points: [
        { title: 'Compose the journey', desc: 'Origin and destination stations, departure date and passenger count drive the search.' },
        { title: 'Read the journey row', desc: 'Each result shows the operator, the class options and the fare per class; transit-time and stops are on the row.' },
        { title: 'Progress to book', desc: 'Selecting a fare opens the passenger-form / seat-selection flow, then payment; the resulting booking behaves like any other reservation.' },
      ],
    },
    flow: {
      lead: 'A rail booking has fewer moving parts than a flight — but seat reservation and class selection replace cabin choice.',
      steps: [
        { title: 'Pick the stations', desc: 'Origin and destination stations (not cities — a city can have several stations, and picking the right one saves the traveler a taxi).' },
        { title: 'Pick the date', desc: 'Rail is much less date-sensitive on price than air; the same fare is usually held for the entire operating day.' },
        { title: 'Read the journey list', desc: 'Departures for the day are listed with duration, stops, class options and price per class.' },
        { title: 'Choose a class', desc: 'Economy, first or business change the seat comfort and (on some operators) the meal / lounge inclusion.' },
        { title: 'Reserve a seat', desc: 'On operators that require reservation, the seat-map picker opens after class selection; open-ticket operators skip this step.' },
        { title: 'Register passengers + pay', desc: 'Standard passenger-form + payment; the e-ticket / QR is attached to the booking.' },
      ],
    },
    modes: {
      lead: 'Rail fares diverge from air on two axes — whether the ticket is tied to a specific seat, and what the class actually buys you.',
      modes: [
        { name: 'Reserved seat', when: 'high-speed / long-haul operators', desc: 'The ticket is bound to a specific train and seat. Miss the train and the ticket is void (or subject to a rebook fee). Seat-map available at booking.' },
        { name: 'Open ticket', when: 'commuter / short-haul routes', desc: 'Valid on any train of a class within a validity window (usually the day of travel). No seat map — first-come-first-served in the class carriage.' },
        { name: 'Economy class', when: 'default price point', desc: 'Standard seating, no additional inclusions. The bulk of rail bookings.' },
        { name: 'First class', when: 'business travelers', desc: 'Wider seat pitch, quieter carriage, sometimes a light meal or lounge access.' },
        { name: 'Business class', when: 'premium long-haul', desc: 'On some operators only. Seat-with-desk, mains power, priority boarding, full meal service.' },
      ],
    },
    examples: [
      {
        title: 'Tehran → Mashhad · overnight · 1st class · 2 pax',
        rows: [
          { label: 'Base fare (per pax) × 2', value: '3,200,000' },
          { label: 'Retail MARKUP · +5%', value: '+160,000', accent: 'success' },
          { label: 'Traveler total', value: '3,360,000', strong: true },
        ],
        note: 'Rail typically bypasses the Wholesale layer — the operator sells at published fare, and the agency\'s only margin is Retail.',
      },
    ],
    related: ['buses-search', 'flights-search', 'markup'],
  },

  'buses-search': {
    title: 'Buses',
    tag: 'Bus search',
    tagline: 'Long-distance coach routes for corridors where road transport wins on price or reach.',
    business: {
      lead: 'Buses covers corridors where a coach is either the only realistic option or clearly cheaper than air / rail. Its commercial role is coverage: it lets the agency book a full trip end-to-end for a customer whose route touches towns without an airport or a rail station.',
      points: [
        { title: 'Coverage over speed', desc: 'Buses reach cities and towns that flights and rail don\'t — critical for pilgrimage, regional business travel and last-mile connections.' },
        { title: 'Simple, cash-friendly fares', desc: 'Bus fares are typically flat per seat with fewer branded classes, which makes bulk / corporate quoting straightforward.' },
        { title: 'Fewer supplier moving parts', desc: 'Most operators expose a static timetable rather than dynamic availability, so search results are stable across a day.' },
      ],
    },
    software: {
      lead: 'A city-to-city search with operator / seat-class filters, currently parked pending backend integration.',
      points: [
        { title: 'Compose the trip', desc: 'Origin and destination cities, departure date and passenger count drive the search.' },
        { title: 'Read the route row', desc: 'Each result shows the operator, duration and price; seat-class options open on selection.' },
        { title: 'Progress to book', desc: 'A selected seat opens the passenger and payment flow; the record joins All Bookings like every other product.' },
      ],
    },
    flow: {
      lead: 'Booking a coach is the simplest transport flow — a fixed timetable, a single seat class per route (usually), and one flat fare per seat.',
      steps: [
        { title: 'Pick origin + destination', desc: 'Cities (not stations — the operator typically has one terminal per city).' },
        { title: 'Pick the date', desc: 'The full day\'s timetable loads — fares don\'t swing across the day.' },
        { title: 'Choose a departure', desc: 'Each row is one departure: operator, seat class, price, and estimated travel time.' },
        { title: 'Choose a seat', desc: 'A seat map opens on operators that support it; otherwise the ticket is assigned on boarding.' },
        { title: 'Register passengers + pay', desc: 'Standard passenger-form + payment; the QR / e-ticket attaches to the booking.' },
      ],
    },
    modes: {
      lead: 'Bus operators mostly ship one class of service, but the seat-selection experience varies.',
      modes: [
        { name: 'Assigned seat', when: 'operators with a seat map', desc: 'Pick a specific seat at booking. The e-ticket carries the seat number and the boarding operator enforces it.' },
        { name: 'Open seating', when: 'operators without a seat map', desc: 'Seat is claimed on boarding. The e-ticket only guarantees a place on that departure.' },
        { name: 'VIP class', when: 'premium long-haul routes', desc: 'Wider seats, sometimes 3-across seating (2+1) instead of standard 2+2, on some operators includes light refreshments.' },
        { name: 'Standard class', when: 'default', desc: 'Standard bus seating, no additional inclusions. Cheapest and most common.' },
      ],
    },
    examples: [
      {
        title: 'Tehran → Isfahan · VIP class · 2 pax',
        rows: [
          { label: 'Base fare (per pax) × 2', value: '1,600,000' },
          { label: 'Retail MARKUP · +6%', value: '+96,000', accent: 'success' },
          { label: 'Traveler total', value: '1,696,000', strong: true },
        ],
        note: 'Bus is a thin-margin product — Retail-only pricing is the norm; the operator publishes the fare, the agency\'s cut is added at Retail.',
      },
    ],
    related: ['trains-search', 'ground', 'markup'],
  },

  // ── Flights funnel (search → results → passengers → seats → payment → ticket, + manual) ──
  'flights-results': {
    title: 'Flight Results',
    tag: 'Flight search',
    tagline: 'Every issuable fare for the trip, merged and ranked — pick the one to ticket.',
    business: {
      lead: 'The results screen is where a quote becomes a choice. Every row is a fare the agency can actually issue (own SIGN or a partner SIGN), the displayed price already carries the Wholesale / Distribution / Retail markups, and the issuing SIGN is visible so the agent knows how the ticket will be fulfilled before committing.',
      points: [
        { title: 'One merged, ranked list', desc: 'Fares from every active supplier are combined and price-ranked, so the agent compares like-for-like instead of tab-hopping between pipes.' },
        { title: 'The price is all-in', desc: 'What shows on the row is what the traveler pays — the pricing engine’s layers are already applied.' },
        { title: 'Compare on what matters', desc: 'Baggage, refundability, stops and departure time drive the decision as much as headline price.' },
      ],
    },
    software: {
      lead: 'A filterable, sortable fare list with a cheapest-by-date fare strip.',
      points: [
        { title: 'Filter and sort', desc: 'Sidebar narrows by stops, price range, airlines, departure / arrival time, fare type and source.' },
        { title: 'Scan the fare strip', desc: 'A mini calendar shows the cheapest fare per date ±7 days; click a date to re-run the search.' },
        { title: 'Read the row, then select', desc: 'Each card shows the segment itinerary, baggage tags and (search-and-book) net / margin / commission; selecting a fare carries its per-pax split into the passenger step.' },
      ],
    },
    related: ['flights-search', 'flights-passengers', 'markup'],
  },

  'flights-manual': {
    title: 'Flight Manual Entry',
    tag: 'Off-channel ticket',
    tagline: 'Record a flight booked outside the platform so it lives in the order book like any other.',
    business: {
      lead: 'Not every ticket comes from the search funnel — some are issued directly with an airline, through a consolidator, or over the phone. Manual entry captures those so the booking, its passengers and its money are tracked, invoiced and reported alongside online sales.',
      points: [
        { title: 'One order book', desc: 'Nothing sold off-system stays invisible — a manual ticket joins All Bookings like any other order.' },
        { title: 'Margin still reports', desc: 'The full fare breakdown (base, taxes, fees, markup, commission) is captured, so profit reporting stays complete.' },
        { title: 'Multi-leg supported', desc: 'One-way, round-trip and multi-city itineraries can all be keyed, with one e-ticket per passenger per leg.' },
      ],
    },
    software: {
      lead: 'A structured form to key in the itinerary, supplier and per-passenger e-tickets.',
      points: [
        { title: 'Build the itinerary', desc: 'Trip type plus leg cards — origin / destination airports, airline, times, aircraft, cabin and seat.' },
        { title: 'Set source and money', desc: 'Booking source / supplier, tour code and currency, with a per-leg fare breakdown.' },
        { title: 'Assign tickets, then continue', desc: 'One e-ticket per passenger is assigned to the legs; continue to the passenger step.' },
      ],
    },
    related: ['flights-search', 'flights-passengers', 'bookings'],
  },

  'flights-passengers': {
    title: 'Flight Passengers',
    tag: 'Traveler details',
    tagline: 'Name every traveler, pick the fare tier, and capture the details the airline needs to ticket.',
    business: {
      lead: 'The passenger step is where an offer becomes a named, ticketable booking. The document and contact details entered here are what the airline validates at issue and the border checks at travel — getting them right first time avoids costly reissues.',
      points: [
        { title: 'Fare tier is a commercial choice', desc: 'Saver / Flex / Plus trade price against baggage, refundability, seat and meal — the agent picks the tier that fits the traveler.' },
        { title: 'Passport data is mandatory', desc: 'Name, date of birth, nationality, passport number and expiry are the airline’s ticketing inputs, not optional extras.' },
        { title: 'CRM prefill saves keystrokes', desc: 'Picking a saved customer autofills their travelers from the CRM, so a returning passenger is entered once and reused.' },
      ],
    },
    software: {
      lead: 'A fare-tier grid plus one identity card per traveler and a trip-wide contact block.',
      points: [
        { title: 'Choose the fare tier', desc: 'A grid compares tiers by baggage, refund, seat, meal and frequent-flyer earn.' },
        { title: 'Fill each passenger card', desc: 'Title, name, DOB, nationality, frequent-flyer number, passport and expiry, plus meal / seat SSR tags.' },
        { title: 'Add contact, then continue', desc: 'Email and dial-code phone for the booking; continue to optional seat selection or straight to payment.' },
      ],
    },
    related: ['flights-results', 'flights-seats', 'flights-payment', 'customers'],
  },

  'flights-seats': {
    title: 'Seat Selection',
    tag: 'Optional add-on',
    tagline: 'Assign seats on an interactive map — or skip and let the airline allocate.',
    business: {
      lead: 'Seat selection is an optional, often paid ancillary. Choosing seats up-front improves the traveler’s experience and can add margin where the map is paid; skipping hands allocation to the airline at check-in with no fee. Either way the booking proceeds.',
      points: [
        { title: 'Always optional', desc: 'Skip and the airline allocates seats at check-in — the booking is never blocked on this step.' },
        { title: 'Paid vs free varies', desc: 'Standard, preferred and extra-legroom seats can carry different fees depending on the fare and carrier.' },
        { title: 'Per-passenger', desc: 'Each traveler on the booking is assigned their own seat.' },
      ],
    },
    software: {
      lead: 'A seat map rendered for the aircraft, with a passenger sidebar and a running assignment count.',
      points: [
        { title: 'Read the map', desc: 'The grid shows available, occupied and selected seats for the aircraft layout.' },
        { title: 'Assign each traveler', desc: 'Pick a seat per passenger from the sidebar; the footer tracks how many are assigned.' },
        { title: 'Skip or continue', desc: 'Save the assignments or skip; either advances to payment. (The same seat map serves buses.)' },
      ],
    },
    related: ['flights-passengers', 'flights-payment'],
  },

  'flights-payment': {
    title: 'Flight Payment',
    tag: 'Capture & issue',
    tagline: 'Choose how the booking is paid, then issue the ticket.',
    business: {
      lead: 'Payment is where the money is committed and the ticket is issued. The method decides how funds move — instantly from the agency wallet or credit line, or via a card / gateway / POS / bank transfer the agent records — and the order summary shows the agent their margin and commission before they commit.',
      points: [
        { title: 'Wallet and credit are instant', desc: 'Both draw against the agency account, so the ticket can issue without waiting on an external rail.' },
        { title: 'Iranian rails supported', desc: 'IPG (Shaparak / ZarinPal and peers), POS card-reader, Sheba bank transfer and 3DS card are all first-class methods.' },
        { title: 'Margin visible before issue', desc: 'On search-and-book, the summary shows net / markup / commission so the agent commits with eyes open.' },
      ],
    },
    software: {
      lead: 'A payment-method picker with method-specific fields and an order-summary sidebar.',
      points: [
        { title: 'Pick the method', desc: 'Wallet, credit line, customer card, cash, IPG, POS or bank transfer — each is a radio card.' },
        { title: 'Fill the method’s form', desc: 'Card 3DS; IPG gateway + phone + reference; POS terminal / RRN / trace; Sheba source & destination IBAN + reference.' },
        { title: 'Add invoice, then issue', desc: 'Bill-to, VAT and PO details on the invoice block; “Pay and issue” creates the order and moves to the ticket.' },
      ],
    },
    modes: {
      lead: 'The same payment methods are available across products — the choice is about how and when the money settles.',
      modes: [
        { name: 'Wallet', when: 'default / recommended', desc: 'Debited in real time from the agency’s prepaid balance. Fastest path to issue; no external step.' },
        { name: 'Credit line', when: 'agency has a credit limit', desc: 'Drawn against the agency’s short-term credit; settled later per the credit terms.' },
        { name: 'IPG link', when: 'customer pays online', desc: 'A payment link via an Iranian gateway (Shaparak / ZarinPal / IDPay…) is sent to the customer; the gateway reference is recorded on return.' },
        { name: 'POS', when: 'card present at the counter', desc: 'Captured on the branch card-reader; terminal ID, RRN and trace number are keyed as proof.' },
        { name: 'Bank transfer (Sheba)', when: 'direct IBAN deposit', desc: 'Source and destination Sheba IBANs plus the transfer reference are recorded for settlement.' },
      ],
    },
    related: ['flights-passengers', 'flights-ticket', 'counter', 'markup'],
  },

  'flights-ticket': {
    title: 'Flight Ticket',
    tag: 'Issued e-ticket',
    tagline: 'The issued e-ticket — PNR, passenger, itinerary and the agent’s profit on the sale.',
    business: {
      lead: 'The ticket page is the proof of sale — the PNR and e-ticket numbers the traveler carries and the agency references for any service action. It also closes the commercial loop, showing the agent the realized margin (sale − cost + commission) on the booking.',
      points: [
        { title: 'The traveler’s document', desc: 'PNR and ticket number, full itinerary, seat and booking status in one place.' },
        { title: 'Servicing starts here', desc: 'Email the itinerary, copy the reference, modify the booking or raise a refund from the quick actions.' },
        { title: 'Profit is realized', desc: 'The financials recap (sale, cost, markup, commission, profit) makes the margin on this sale explicit.' },
      ],
    },
    software: {
      lead: 'A voucher-style ticket with the flight, passenger and financials laid out.',
      points: [
        { title: 'Header and passenger band', desc: 'PNR, e-Ticket label, passenger name, ticket number, frequent-flyer and issue time.' },
        { title: 'Itinerary grid', desc: 'Flight number, aircraft, cabin, fare tier, departure / arrival times and stops.' },
        { title: 'Financials and actions', desc: 'A margin sidebar plus email / copy / modify / refund controls.' },
      ],
    },
    related: ['flights-payment', 'bookings', 'booking-detail', 'refunds'],
  },

  // ── Trains funnel (search → results → seats & pax → payment → ticket, + manual) ──
  'trains-results': {
    title: 'Train Results',
    tag: 'Rail search',
    tagline: 'Every departure for the day, ranked — pick the train and class to book.',
    business: {
      lead: 'Rail results are steadier than air: the day’s timetable and its fares hold, so the agent compares on time, class and refundability rather than chasing a moving price. Each row is a bookable departure with its class options and seats-remaining.',
      points: [
        { title: 'Fares are stable', desc: 'The same fare usually holds across the operating day, so a quote stays valid longer than a flight fare.' },
        { title: 'Compare class, not carrier', desc: 'The decision is between wagon types and comfort on the route, not between competing airlines.' },
        { title: 'Refundability up-front', desc: 'Each row is tagged refundable / partial / non-refundable before the agent commits.' },
      ],
    },
    software: {
      lead: 'A journey list with operator, times, wagon type and per-class price, plus a fare strip.',
      points: [
        { title: 'Filter the day', desc: 'Sidebar narrows by sort, hide-sold-out, operator, price histogram, wagon type and time-of-day bucket.' },
        { title: 'Scan the fare strip', desc: 'A ±7-day mini calendar shows the cheapest price per date; click to re-search.' },
        { title: 'Select a departure', desc: 'Rows show operator, times, stops, seats-left and refund tags; selecting advances to seats & passengers.' },
      ],
    },
    related: ['trains-search', 'trains-passengers', 'markup'],
  },

  'trains-manual': {
    title: 'Train Manual Entry',
    tag: 'Off-channel ticket',
    tagline: 'Record a rail ticket booked outside the platform into the order book.',
    business: {
      lead: 'Some rail tickets are bought directly at a station or an operator counter. Manual entry brings them into the same order book so they’re tracked, invoiced and reported alongside online rail sales — no booking falls through the cracks.',
      points: [
        { title: 'One order book', desc: 'An off-channel rail ticket joins All Bookings like any other order.' },
        { title: 'Margin still reports', desc: 'The fare breakdown is captured so profit reporting stays complete.' },
        { title: 'Station-level itinerary', desc: 'Legs are keyed by station, matching how rail is actually sold.' },
      ],
    },
    software: {
      lead: 'The shared transport manual-entry form, keyed for rail.',
      points: [
        { title: 'Key the journey', desc: 'Legs with stations, operator, times and class.' },
        { title: 'Set source and money', desc: 'Supplier, currency and a per-leg fare breakdown.' },
        { title: 'Assign tickets, then continue', desc: 'One ticket per passenger; continue to the passenger step.' },
      ],
    },
    related: ['trains-search', 'trains-passengers', 'bookings'],
  },

  'trains-passengers': {
    title: 'Train Seats & Passengers',
    tag: 'Class, seat & pax',
    tagline: 'Pick the class, choose coach seats on the map, and name the travelers.',
    business: {
      lead: 'Rail folds the class choice and the seat map into one step. Class sets the comfort and inclusions; the coach / seat map binds the ticket to a physical place (many operators enforce gender-aware compartments); and the traveler details finish the booking.',
      points: [
        { title: 'Class is the lever', desc: 'Standard / Premier / Comfort trade price for perks — the agent’s main decision on rail.' },
        { title: 'The seat binds the ticket', desc: 'A coach and seat are chosen on the map; reserved vs open depends on the operator.' },
        { title: 'Fewer document fields than air', desc: 'Rail needs name, contact and loyalty — not the full passport / APIS set a flight requires.' },
      ],
    },
    software: {
      lead: 'A class picker, an interactive coach / seat map, and a passenger form.',
      points: [
        { title: 'Choose the class', desc: 'Cards compare price and perks per travel class.' },
        { title: 'Pick coach and seat', desc: 'Switch coaches and pick seats on a grid (available / occupied / selected), with an amenity legend.' },
        { title: 'Name the travelers', desc: 'Title, name, email, phone and loyalty ID per passenger; continue to payment.' },
      ],
    },
    related: ['trains-results', 'trains-payment', 'customers'],
  },

  'trains-payment': {
    title: 'Train Payment',
    tag: 'Capture & issue',
    tagline: 'Choose how the rail booking is paid, then issue the ticket — before the seat lock expires.',
    business: {
      lead: 'Payment commits the fare and issues the rail ticket. Because the seat is held only for a short lock window after selection, completing payment promptly is what secures it. The method decides how funds move — wallet / credit instantly, or a gateway / POS / transfer the agent records.',
      points: [
        { title: 'The seat is on a timer', desc: 'Selection places a short hold; pay before it expires or the seat is released.' },
        { title: 'Wallet and credit are instant', desc: 'Both draw against the agency account for immediate issue.' },
        { title: 'Iranian rails supported', desc: 'IPG, POS and Sheba bank transfer are all available alongside wallet and credit.' },
      ],
    },
    software: {
      lead: 'A payment-method picker with method-specific fields and a payment summary.',
      points: [
        { title: 'Pick the method', desc: 'Wallet, credit, card, IPG, POS or Sheba transfer.' },
        { title: 'Fill the method’s form', desc: 'IPG gateway + phone + reference; POS terminal / RRN / trace; Sheba source & destination IBAN + reference.' },
        { title: 'Note the lock, then issue', desc: 'A seat-lock banner shows what’s held; “Pay and issue” charges and moves to the ticket.' },
      ],
    },
    modes: {
      lead: 'The same payment methods are available across products — the choice is about how and when the money settles.',
      modes: [
        { name: 'Wallet', when: 'default / recommended', desc: 'Debited in real time from the agency’s prepaid balance. Fastest path to issue.' },
        { name: 'Credit line', when: 'agency has a credit limit', desc: 'Drawn against short-term credit; settled later per terms.' },
        { name: 'IPG link', when: 'customer pays online', desc: 'A gateway link (Shaparak / ZarinPal…) is sent to the customer; the reference is recorded on return.' },
        { name: 'POS', when: 'card present at the counter', desc: 'Captured on the branch reader; terminal ID, RRN and trace are keyed.' },
        { name: 'Bank transfer (Sheba)', when: 'direct IBAN deposit', desc: 'Source / destination Sheba IBAN and the transfer reference are recorded.' },
      ],
    },
    related: ['trains-passengers', 'trains-ticket', 'counter'],
  },

  'trains-ticket': {
    title: 'Train Ticket',
    tag: 'Issued e-ticket',
    tagline: 'The issued rail e-ticket — booking reference, coach / seat, QR and the sale’s profit.',
    business: {
      lead: 'This is proof of the rail sale — the booking reference and QR / barcode the traveler scans to board, plus the confirmed coach and seat. It also closes the commercial loop, showing the agent the realized margin on the booking.',
      points: [
        { title: 'Board with the QR', desc: 'The reference and barcode are what the traveler presents to board.' },
        { title: 'Coach and seat confirmed', desc: 'The exact coach and seat (and its facing / status) are on the ticket.' },
        { title: 'Profit is realized', desc: 'The financials recap makes the margin on this sale explicit.' },
      ],
    },
    software: {
      lead: 'A ticket with the journey, coach / seat and financials.',
      points: [
        { title: 'Header and passenger band', desc: 'Booking reference, passenger name, ticket number, class and issue date.' },
        { title: 'Journey and seat', desc: 'Operator, service number, departure / arrival stations and duration, plus the coach / seat row and barcode.' },
        { title: 'Financials and actions', desc: 'A margin sidebar plus email / copy / modify / refund controls.' },
      ],
    },
    related: ['trains-payment', 'bookings', 'booking-detail', 'refunds'],
  },

  // ── Hotels funnel (search → results [book in drawer] → voucher) ──
  'hotels-results': {
    title: 'Hotel Results',
    tag: 'Hotel search',
    tagline: 'Properties for the stay, ranked — with the sell mode and refund policy visible before you commit.',
    business: {
      lead: 'The results screen shows every bookable property for the search, blending own-contracted inventory with connected supply. The badges that matter commercially — Instant vs On-Request, refundable vs not — are on the card, so the agent picks knowing how the booking will confirm and when money moves.',
      points: [
        { title: 'Own + connected supply merged', desc: 'Rooms from the platform’s Hotel Property module and from external suppliers are ranked in one list.' },
        { title: 'Sell mode is visible', desc: 'Instant confirms at the click; On-Request holds the booking until a supplier accepts — the badge tells the agent which they’re getting.' },
        { title: 'Refund policy up-front', desc: 'Free-cancel vs non-refundable is a filter and a tag, so the agent quotes the right policy.' },
      ],
    },
    software: {
      lead: 'A filterable property list with a fare strip and a room-selection drawer.',
      points: [
        { title: 'Filter properties', desc: 'Sidebar narrows by sort, star rating, price range, refund policy, area and supplier, plus free-text on name / area / chain.' },
        { title: 'Scan the fare strip', desc: 'A 7-day nightly-price calendar around check-in; click a date to re-search.' },
        { title: 'Select a room to book', desc: 'Cards show best nightly rate vs rack, review score and refund tag; “Select room” opens the booking drawer.' },
      ],
    },
    flow: {
      lead: 'From the results page, the booking finishes in a drawer (not separate pages) that lands on the voucher.',
      steps: [
        { title: 'Select room & rate', desc: 'Pick a room type and rate plan; total covers all nights plus taxes, with the refund policy shown.' },
        { title: 'Guest details', desc: 'One form per guest per room (title, name, DOB, nationality, passport); a saved customer prefills from the CRM. The lead guest carries the booking contact.' },
        { title: 'Payment (+ add-ons)', desc: 'Pick the payment method; on own inventory, optional ancillaries (parking, spa…) can be added here.' },
        { title: 'Review & confirm', desc: 'A final summary of rooms, guests and the total; confirming creates the order and issues the voucher.' },
      ],
    },
    related: ['hotels-search', 'hotels-ticket', 'supply-properties', 'markup'],
  },

  'hotels-ticket': {
    title: 'Hotel Voucher',
    tag: 'Issued voucher',
    tagline: 'The confirmed hotel voucher — property, stay dates, guest roster and the charges.',
    business: {
      lead: 'The voucher is the guest’s proof of a confirmed stay and the document the hotel honors at check-in. It consolidates the property, the rate plan and board, the check-in / out dates and every guest’s document into one record the agency can email or print.',
      points: [
        { title: 'Proof for check-in', desc: 'The reference plus the per-room guest roster (with documents) is what the hotel verifies on arrival.' },
        { title: 'Board and refund on the record', desc: 'The rate plan’s board and cancellation policy are stated on the voucher, not buried in a contract.' },
        { title: 'Servicing from here', desc: 'Email the itinerary, copy the reference, or modify / cancel the booking.' },
      ],
    },
    software: {
      lead: 'A voucher with the property, a stay strip, the per-room guest roster and charges.',
      points: [
        { title: 'Header and property', desc: 'A “voucher issued” badge and reference, the property with Confirmed / board / refund tags, and email / print / download.' },
        { title: 'Stay strip', desc: 'Check-in date (from 15:00), the number of nights, and check-out (by 12:00).' },
        { title: 'Guests and charges', desc: 'Per-room guest roster with documents, plus a charges sidebar (room base, taxes, total).' },
      ],
    },
    related: ['hotels-results', 'bookings', 'booking-detail'],
  },

  // ── Tours — package authoring (the sell flow lives on the `tours` page via drawers) ──
  'tours-new': {
    title: 'Tour Package Builder',
    tag: 'Package authoring',
    tagline: 'Assemble a multi-service package — itinerary, flights, hotels, inclusions and per-occupancy pricing — then publish it to sell.',
    business: {
      lead: 'This is the supply side of Tours: where an operator builds the product agents later sell. A package bundles flights, hotels, transfers and activities into one SKU with its own itinerary, departure window and packaged price — so it can be marketed on a headline price and its margin managed as a unit rather than defended line by line.',
      points: [
        { title: 'One SKU, many components', desc: 'Flights and a hotel are always included; visa, transfers and insurance are optional toggles — all sold as a single record.' },
        { title: 'Priced per occupancy', desc: 'Each hotel basket is priced across occupancy roles (Adult, Adult-single, Child-with-bed, Child-no-bed, Infant), so a family or a solo traveler each get the right price.' },
        { title: 'Publish state controls sale', desc: 'A package moves Inactive → Preview → Active; only Active departures (not past) can be booked from the Tours page.' },
      ],
    },
    software: {
      lead: 'A step wizard from basics to publish, with each step validated before you advance.',
      points: [
        { title: 'Basics, itinerary, components', desc: 'Name, cities, dates, category and badges; a day-by-day plan with per-day overnight city; and an include / exclude list from the catalogue or free-text.' },
        { title: 'Flights and hotel baskets', desc: 'Attach a flight (search-and-book in tour mode, or manual) and build multi-city hotel baskets (city + hotel + room).' },
        { title: 'Pricing and publish', desc: 'A per-basket × per-occupancy price matrix, then publish with distribution flags (featured, weekly mail, market restriction, B2C white-label).' },
      ],
    },
    related: ['tours', 'flights-search', 'hotels-search', 'supply-properties'],
  },

  visa: {
    title: 'Visa',
    tag: 'Visa services',
    tagline: 'Bookable visa applications with catalogue, eligibility and officer workspace behind the counter.',
    business: {
      lead: 'Visa turns entry-permission processing into a bookable service. The agency sells the visa the same way it sells a flight or a hotel — as a product with a price, a lead time and a defined delivery — with a back-office officer flow behind the scenes that actually collects documents, submits to the consulate and returns the decision.',
      points: [
        { title: 'A visa is a product', desc: 'Each destination × visa type has a catalogue entry with pricing, lead time and documentary requirements; the agency\'s markup layers on top.' },
        { title: 'Eligibility gates the sale', desc: 'A traveler\'s nationality, purpose of visit and validity window determine which catalogue entries are actually sellable — the search only offers valid combinations.' },
        { title: 'Money is captured, service is delivered', desc: 'Payment is taken at booking; the officer workspace tracks progress from documents-received to decision-returned.' },
      ],
    },
    software: {
      lead: 'A visa-catalogue search plus a back-office officer workspace for the fulfilment side.',
      points: [
        { title: 'Search the catalogue', desc: 'Destination, visa type and traveler nationality drive the results; only sellable combinations appear.' },
        { title: 'Book the visa', desc: 'Selecting an entry opens the traveler / document-upload form and payment; the result is a visa booking.' },
        { title: 'Track through delivery', desc: 'The officer workspace shows every open case, its stage, its documents and its consulate-side status — the agent uses this to update the customer.' },
      ],
    },
    flow: {
      lead: 'A visa booking is two halves — the customer-facing sale, then the back-office fulfilment through decision.',
      steps: [
        { title: 'Filter the catalogue', desc: 'Destination country, visa type (tourist / business / transit / student) and traveler nationality drive eligibility; ineligible combos are hidden.' },
        { title: 'Read the entry', desc: 'Each catalogue entry shows the fee, the lead time, the documentary requirements and the consulate\'s decision channel.' },
        { title: 'Register the traveler', desc: 'Traveler details (passport, dates of travel, purpose, supporting details) drive the case file.' },
        { title: 'Upload documents', desc: 'Passport scan, photo, invitation, financial proof — the required set varies per catalogue entry.' },
        { title: 'Pay and open the case', desc: 'Payment is captured at booking. The case opens in the officer workspace at the "documents received" stage.' },
        { title: 'Officer fulfilment', desc: 'Officer reviews documents, submits to the consulate, tracks status, receives the decision, and closes the case with a visa document or a refusal.' },
        { title: 'Deliver to the customer', desc: 'The visa document (or the refusal explanation) is attached to the booking and made available to the agent to share.' },
      ],
    },
    modes: {
      lead: 'Visa entries are catalogued by visa type; the flow adapts per type.',
      modes: [
        { name: 'Tourist', when: 'leisure travel', desc: 'Standard visitor visa. Lower fee, faster lead time on most destinations. Documentary set is usually passport + photo + itinerary.' },
        { name: 'Business', when: 'work / conference visits', desc: 'Requires an invitation letter from the host organization. Higher fee, similar lead time, sometimes an in-person appointment at the consulate.' },
        { name: 'Transit', when: 'stopover in the destination', desc: 'Shortest validity, cheapest fee. Only valid for the transit window. Not all destinations offer one; the search filters them accordingly.' },
        { name: 'Student', when: 'formal admission at a local institution', desc: 'Requires proof of admission and often a financial guarantee. Longest lead time; highest fee. Officer workspace tracks the extended documentary loop.' },
      ],
    },
    examples: [
      {
        title: 'Turkey tourist visa · 1 pax · 21-day lead time',
        rows: [
          { label: 'Consulate fee', value: '4,500,000' },
          { label: 'Service fee (agency processing)', value: '1,500,000' },
          { label: 'Retail MARKUP · +10% of gross', value: '+600,000', accent: 'success' },
          { label: 'Traveler total', value: '6,600,000', strong: true },
        ],
        note: 'The consulate fee is pass-through; the agency margins on the service fee and the retail markup, not on the consulate cost.',
      },
    ],
    related: ['flights-search', 'reference-data', 'markup'],
  },

  cip: {
    title: 'CIP',
    tag: 'Airport concierge',
    tagline: 'Meet-and-assist and VIP airport concierge services sold alongside a trip.',
    business: {
      lead: 'CIP (Commercially Important Person) is airport concierge — a meet-and-assist service that pairs a customer with an airport handler on arrival, transit or departure. Commercially it\'s a small, high-margin add-on the agency can bolt onto any qualifying trip and mostly sold to reseller agencies rather than direct to travelers.',
      points: [
        { title: 'Ancillary revenue', desc: 'A concierge booking is cheap to fulfil, high-margin and doesn\'t compete with the core flight/hotel sale — pure additional revenue.' },
        { title: 'Reseller-only', desc: 'The CIP catalogue is exposed to agencies (not customers) — this is a wholesale product the reseller re-prices to their own end customer.' },
        { title: 'Owned by cip-service', desc: 'CIP catalogue data is owned by the dedicated cip-service, not content-service, so pricing and availability are managed independently from the rest of reference data.' },
      ],
    },
    software: {
      lead: 'A dedicated concierge-catalogue search and booking screen backed by cip-service.',
      points: [
        { title: 'Search the catalogue', desc: 'Airport, service type (arrival / departure / transit) and traveler count drive the results.' },
        { title: 'Book the service', desc: 'Selecting a service opens the traveler-form and payment flow; the result is a CIP booking that appears alongside every other product in All Bookings.' },
        { title: 'Priced end-to-end', desc: 'Money is captured at booking; ledger reconciliation to the accounting stack is on the Phase-3 roadmap.' },
      ],
    },
    flow: {
      lead: 'A CIP booking is short — the traveler picks an airport, a moment (arrival / departure / transit) and a service level.',
      steps: [
        { title: 'Pick the airport', desc: 'Airport code determines which handler network is available; not every airport has coverage.' },
        { title: 'Pick the moment', desc: 'Arrival, departure or transit — the handler starts and ends at different points inside the terminal for each.' },
        { title: 'Pick the service level', desc: 'Meet-and-assist, VIP, or full lounge access — each tier bundles more privileges (fast-track, buggy, lounge time).' },
        { title: 'Set the flight details', desc: 'Flight number and date so the handler can meet the correct traveler.' },
        { title: 'Register travelers + pay', desc: 'Traveler names + payment; the handler receives the assignment and the customer receives a confirmation with the meeting point.' },
      ],
    },
    modes: {
      lead: 'CIP is bought at one of three moments in a trip, and at one of a few service levels — the catalogue is a matrix of those.',
      modes: [
        { name: 'Arrival', when: 'incoming flight', desc: 'Handler meets the traveler at the gate / jet bridge, handles immigration and baggage, escorts to landside. Popular with VIPs and first-time visitors.' },
        { name: 'Departure', when: 'outbound flight', desc: 'Handler meets the traveler landside, handles check-in and security, escorts to the gate or the lounge.' },
        { name: 'Transit', when: 'connecting flight', desc: 'Handler meets between flights, handles transfer between terminals and gates. Especially valuable on tight connections.' },
        { name: 'Meet & Assist', when: 'basic tier', desc: 'A handler and physical assistance — the essential service.' },
        { name: 'VIP', when: 'premium tier', desc: 'Adds fast-track through immigration and security, buggy transport within the terminal, sometimes lounge access.' },
      ],
    },
    examples: [
      {
        title: 'DXB arrival · VIP · 2 pax',
        rows: [
          { label: 'Base handler fee (per pax) × 2', value: '2,800,000' },
          { label: 'Wholesale MARKUP · +8% (handler agency)', value: '+224,000', accent: 'info' },
          { label: 'Retail MARKUP · +15% of gross', value: '+453,600', accent: 'success' },
          { label: 'Traveler total', value: '3,477,600', strong: true },
        ],
        note: 'CIP is a high-margin ancillary — 20–30% blended markup is normal here, versus 5–10% on flights.',
      },
    ],
    related: ['flights-search', 'reference-data', 'markup'],
  },

  ground: {
    title: 'Ground Transport',
    tag: 'Ground services',
    tagline: 'Point-to-point transfers, chauffeur services and short-haul road transport.',
    business: {
      lead: 'Ground Transport sells the road leg of a trip — airport transfers, chauffeur pickups, day-hire cars — as a bookable product. It\'s the "get from A to B on the ground" companion to the flight or hotel sale, and it lets the agency close out a customer\'s whole trip in one basket.',
      points: [
        { title: 'Closes the trip', desc: 'A customer who has already booked a flight and a hotel with the agency has an obvious next question — how do I get from one to the other. Ground answers it in-house.' },
        { title: 'Attaches to a flight', desc: 'Airport transfers are typically bought after the flight is selected; the pickup time can inherit from the flight\'s arrival, reducing operator effort.' },
        { title: 'Layered pricing applies', desc: 'The same Wholesale / Distribution / Retail engine that prices a flight also prices a ground service — a partner-scoped rule works here too.' },
      ],
    },
    software: {
      lead: 'A pickup / drop-off search with vehicle-class options and a booking flow.',
      points: [
        { title: 'Compose the leg', desc: 'Pickup location, drop-off location, date and passenger count drive the search; vehicle class (sedan / SUV / van) narrows the choice.' },
        { title: 'Read the vehicle row', desc: 'Each result shows the operator, vehicle class, price and estimated duration.' },
        { title: 'Progress to book', desc: 'A selected vehicle opens the passenger / pickup-details form and payment; the record joins All Bookings.' },
      ],
    },
    flow: {
      lead: 'A ground booking is short and often attaches to an existing flight — pickup location, drop-off, timing, vehicle class.',
      steps: [
        { title: 'Pick pickup + drop-off', desc: 'City-to-city, airport-to-hotel, or point-to-point. Airport pickup pre-fills from a matched flight in the same PNR.' },
        { title: 'Pick date + time', desc: 'The pickup timestamp — for airport transfers it can inherit from the flight\'s arrival (adjusted for a standard buffer).' },
        { title: 'Choose vehicle class', desc: 'Sedan for solo / couple, SUV for family, van for a group. Vehicle class drives capacity, luggage and price.' },
        { title: 'Register passengers + pay', desc: 'Lead passenger name and phone + payment; the driver is dispatched and the traveler receives the pickup details.' },
      ],
    },
    modes: {
      lead: 'Ground services split by trip shape — a one-shot transfer or an ongoing rental — and by vehicle class.',
      modes: [
        { name: 'Airport transfer', when: 'inbound / outbound flight', desc: 'One-shot pickup from airport to hotel (or reverse). Cheapest and most common. Pickup timing inherits from the flight arrival.' },
        { name: 'Point-to-point', when: 'city → city or inter-city hop', desc: 'One drop-off elsewhere in the city or region. Price scales with distance.' },
        { name: 'Chauffeur / hourly', when: 'business meetings / VIP day', desc: 'Driver + vehicle for a booked block of hours. Traveler dictates the day\'s route.' },
        { name: 'Day hire', when: 'sightseeing over a full day', desc: 'Full-day vehicle + driver, usually with a maximum kilometre allowance. Higher price than an hourly block.' },
      ],
    },
    examples: [
      {
        title: 'DXB airport → hotel · SUV · 2 pax + luggage',
        rows: [
          { label: 'Base transfer fare', value: '600,000' },
          { label: 'Wholesale MARKUP · +5% (operator margin)', value: '+30,000', accent: 'info' },
          { label: 'Retail MARKUP · +10% of gross', value: '+63,000', accent: 'success' },
          { label: 'Traveler total', value: '693,000', strong: true },
        ],
        note: 'A ground service attached to an existing flight PNR shares the booking record — one payment, one voucher, one refund flow.',
      },
    ],
    related: ['flights-search', 'buses-search', 'markup'],
  },

  sim: {
    title: 'SIM Cards',
    tag: 'Travel connectivity',
    tagline: 'Data-only travel SIMs / eSIMs sold as a paid add-on to any outbound trip.',
    business: {
      lead: 'SIM Cards sells a data plan for the destination alongside the trip itself. The customer avoids roaming fees, the agency earns a small margin, and the fulfilment (physical SIM shipped, or eSIM QR delivered) is thin enough to keep the cost of goods low.',
      points: [
        { title: 'A pure ancillary', desc: 'The margin is small per unit but the attach cost is negligible; it\'s incremental revenue on every outbound leisure trip.' },
        { title: 'Physical + eSIM', desc: 'Physical SIMs need a delivery address and lead time; eSIMs deliver as a QR code immediately at booking, which changes the flow.' },
        { title: 'Destination-scoped', desc: 'Each plan is tied to a country or region and a data allowance / validity window — the catalogue is a matrix of those combinations.' },
      ],
    },
    software: {
      lead: 'A destination + plan search backed by the SIM catalogue.',
      points: [
        { title: 'Search the catalogue', desc: 'Destination country and validity window drive the results; data allowance narrows further.' },
        { title: 'Read the plan row', desc: 'Each row shows the operator, data allowance, validity and price; the SIM type (physical vs eSIM) affects fulfilment.' },
        { title: 'Book the plan', desc: 'Selecting a plan opens the traveler / delivery-details form and payment; eSIM records deliver instantly, physical SIMs schedule a delivery.' },
      ],
    },
    flow: {
      lead: 'A SIM booking is one of the shortest inventory flows — destination, allowance, SIM type, deliver.',
      steps: [
        { title: 'Pick the destination', desc: 'Country or region — the catalogue is indexed by coverage area, not by operator.' },
        { title: 'Pick the validity window', desc: 'Trip length drives which plans apply — a 7-day plan won\'t fit a 21-day trip.' },
        { title: 'Pick the allowance', desc: 'Data budget (unlimited / 10GB / 20GB) narrows further; unlimited plans usually throttle after a fair-use cap.' },
        { title: 'Choose SIM type', desc: 'Physical SIM (needs delivery address + lead time) or eSIM (QR delivered on booking). Traveler\'s phone compatibility determines the choice.' },
        { title: 'Register + pay', desc: 'Traveler + delivery address (physical only) + payment. eSIM QR shows immediately on the confirmation; physical SIM shipping is scheduled.' },
      ],
    },
    modes: {
      lead: 'Two dimensions matter — the physical vs eSIM fulfilment, and the geographical coverage.',
      modes: [
        { name: 'Physical SIM', when: 'traveler\'s phone doesn\'t support eSIM', desc: 'A nano / micro SIM shipped to the traveler\'s address (or picked up at a partner desk). Needs 3–5 days lead time on most routes, so book early.' },
        { name: 'eSIM', when: 'traveler\'s phone supports eSIM (most modern smartphones)', desc: 'QR code delivered on booking — no shipping, no lead time. Traveler scans on arrival and the profile activates. Zero cost of goods to the agency.' },
        { name: 'Country-specific', when: 'single-destination trip', desc: 'Plan is valid in one country. Cheapest per-GB rate.' },
        { name: 'Regional', when: 'multi-country trip (e.g. Europe hop)', desc: 'Plan is valid across a defined region (EU-wide, Asia-Pacific, GCC). Higher per-GB rate but no need to swap SIMs at each border.' },
        { name: 'Global', when: 'multi-region long trip', desc: 'Plan is valid in a broad country list — 100+ countries. Highest per-GB rate; fits round-the-world or business travelers.' },
      ],
    },
    examples: [
      {
        title: 'Turkey · 10-day 10GB · eSIM · 1 pax',
        rows: [
          { label: 'Supplier plan cost', value: '600,000' },
          { label: 'Retail MARKUP · +30% (high-margin ancillary)', value: '+180,000', accent: 'success' },
          { label: 'Traveler total', value: '780,000', strong: true },
        ],
        note: 'eSIM has zero fulfilment cost — the QR is generated by the supplier and delivered on confirmation, so the whole markup drops to margin.',
      },
      {
        title: 'Same trip · physical SIM',
        rows: [
          { label: 'Supplier plan cost', value: '600,000' },
          { label: 'Shipping', value: '100,000' },
          { label: 'Retail MARKUP · +25% of gross', value: '+175,000', accent: 'success' },
          { label: 'Traveler total', value: '875,000', strong: true },
        ],
        note: 'Physical SIMs add shipping cost and shipping lead time — the eSIM version is generally the better sell when the customer\'s phone supports it.',
      },
    ],
    related: ['visa', 'insurance', 'flights-search'],
  },

  insurance: {
    title: 'Travel Insurance',
    tag: 'Trip protection',
    tagline: 'Bookable travel-insurance policies sold as an add-on to a trip.',
    business: {
      lead: 'Travel Insurance protects the customer against trip disruptions (delays, cancellations, medical, baggage) and gives the agency an ancillary revenue line. Some destinations require valid coverage as a visa condition, so it can also be a compliance product rather than a pure optional.',
      points: [
        { title: 'Optional and mandatory both exist', desc: 'For some visas coverage is required to qualify; for pure leisure trips it\'s an optional the agent up-sells at close.' },
        { title: 'Tiered plans', desc: 'A plan is defined by its coverage limits and duration — the customer picks a tier that matches their risk tolerance and budget.' },
        { title: 'Underwritten externally', desc: 'The platform doesn\'t underwrite the policy — it sells a third-party product and books the resulting policy record on the customer\'s trip.' },
      ],
    },
    software: {
      lead: 'A traveler + destination search over the connected insurance catalogue.',
      points: [
        { title: 'Compose the coverage', desc: 'Destination, travel dates and traveler ages drive the results; coverage tier narrows the plan.' },
        { title: 'Read the plan row', desc: 'Each result shows the underwriter, the coverage limits and the price; policy documents open on selection.' },
        { title: 'Book the policy', desc: 'Selecting a plan opens the traveler-form and payment; the policy record and its documents are attached to the customer\'s trip.' },
      ],
    },
    flow: {
      lead: 'A policy booking is short — destination, travel dates, traveler ages, tier, pay.',
      steps: [
        { title: 'Pick the trip shape', desc: 'Destination country / region, travel dates, and the age of every traveler (rate scales with age band).' },
        { title: 'Choose a coverage tier', desc: 'Basic covers essentials only; Standard adds baggage / delay; Premium adds full medical + trip-cancellation. Pick to match the trip\'s value at risk.' },
        { title: 'Read the plan', desc: 'Each plan lists coverage limits per line (medical, cancellation, baggage) and the exclusions. Policy documents open on the same screen.' },
        { title: 'Register travelers + pay', desc: 'Traveler names, DOBs and passport numbers + payment; the policy is issued at booking.' },
        { title: 'Get the policy documents', desc: 'The policy PDF + certificate attach to the booking. Some destinations require the certificate at consulate submission.' },
      ],
    },
    modes: {
      lead: 'Insurance is priced by two axes — coverage tier (what\'s covered) and trip shape (single trip vs annual).',
      modes: [
        { name: 'Basic tier', when: 'domestic / short leisure trips', desc: 'Medical emergency + repatriation. Baggage and cancellation not included. Cheapest premium.' },
        { name: 'Standard tier', when: 'most leisure travel', desc: 'Adds baggage loss, trip delay, and limited cancellation. The most-sold tier.' },
        { name: 'Premium tier', when: 'expensive trips or older travelers', desc: 'Adds full medical (higher limits), full cancellation-for-any-reason, adventure activities. Highest premium.' },
        { name: 'Single-trip', when: 'one trip in the coming weeks', desc: 'Covers a specific date window (start → end of trip). Cheapest for occasional travelers.' },
        { name: 'Annual multi-trip', when: 'frequent business or leisure travel', desc: 'Covers every trip within a year, up to a per-trip length cap (usually 30–60 days). Cheaper on 3+ trips a year than buying single-trip each time.' },
      ],
    },
    examples: [
      {
        title: 'Turkey · 10-day trip · Standard tier · 2 adults + 1 child',
        rows: [
          { label: 'Base premium (adult) × 2', value: '1,400,000' },
          { label: 'Base premium (child)', value: '400,000' },
          { label: 'Subtotal', value: '1,800,000' },
          { label: 'Retail MARKUP · +20% commission', value: '+360,000', accent: 'success' },
          { label: 'Traveler total', value: '2,160,000', strong: true },
        ],
        note: 'Insurance commissions are typically 15–25% of premium — a solid ancillary margin per booking, and one policy covers everyone on the trip.',
      },
    ],
    related: ['visa', 'sim', 'flights-search'],
  },

  // ── RESERVATIONS section ────────────────────────────────────────────────

  bookings: {
    title: 'All Bookings',
    tag: 'Order book',
    tagline: 'Every order the agency has made, across every product, in one list.',
    business: {
      lead: 'All Bookings is the operational record of everything sold — flights, hotels, tours, transport, visas, CIP — as unified orders. It’s where staff find a customer’s booking to service it: reissue, add extras, cancel, refund or simply check where it stands.',
      points: [
        { title: 'One order, many products', desc: 'A single order can bundle a flight, a hotel and an ancillary — each is an item under the same reference / PNR.' },
        { title: 'Status tells you what to do', desc: 'Draft → Held → Confirmed → Paid, plus In-review (an On-Request stay awaiting confirmation) and Cancelled — the chip shows where a booking sits.' },
        { title: 'The service surface', desc: 'Every post-sale action (cancel, refund, charges, documents) starts by finding the order here.' },
      ],
    },
    software: {
      lead: 'A searchable, filterable table of orders — and the target of the global top-bar search.',
      points: [
        { title: 'Find fast', desc: 'Search by reference, PNR, passenger or contact; filter by product, status or date.' },
        { title: 'Global search lands here', desc: 'A top-bar search that isn’t a unique hit lists its matches here; a unique hit jumps straight to the booking detail.' },
        { title: 'Open to service', desc: 'Click a row to open the full booking detail and act on it.' },
      ],
    },
    lifecycles: {
      lead: 'Every order rides the same backbone — created, paid, then fulfilled — but the "fulfilled" state is named for what the product delivers. Transport issues a travel document, so its success state is «Ticketed». Hotels and most services secure a reservation with no ticket, so theirs is «Confirmed». A visa is decided rather than ticketed or confirmed, so its success state is «Approved». All three are the same milestone: the sale is real and an accounting sale voucher posts. From there a booking can be cancelled and refunded, and unhappy paths (payment failure, an expired hold, an operator rejection, a visa refusal) end in their own terminal states.',
      legend: [
        { label: 'In progress', kind: 'neutral' },
        { label: 'Awaiting an action', kind: 'warn' },
        { label: 'Sale is real → posts a voucher', kind: 'success' },
        { label: 'Unhappy / terminal', kind: 'danger' },
      ],
      items: [
        {
          product: 'Flights, Trains & Buses',
          success: 'Ticketed',
          lead: 'Transport holds inventory as a PNR, then issues a numbered ticket. «Ticketed» is the point of no easy return — the document is emitted and the sale posts.',
          path: [
            { status: 'Draft', kind: 'neutral', note: 'cart built' },
            { status: 'Held', kind: 'warn', note: 'PNR holds a seat' },
            { status: 'Pending payment', kind: 'neutral' },
            { status: 'Ticketed', kind: 'success', note: 'e-ticket / PNR issued · voucher posts' },
          ],
          branches: [
            { from: 'Held', nodes: [{ status: 'Expired', kind: 'danger' }], desc: 'The hold window lapses (a sweeper runs every ~60s) and the seat is auto-released back to the supplier.' },
            { from: 'Pending payment', nodes: [{ status: 'Failed', kind: 'danger' }], desc: 'Payment is declined or the supplier fare expires on the retry — nothing is ticketed.' },
            { from: 'Ticketed', nodes: [{ status: 'Cancelled', kind: 'danger' }, { status: 'Refunded', kind: 'neutral' }], desc: 'Post-sale: a cancellation quote runs, then any captured money is returned less penalty.' },
          ],
        },
        {
          product: 'Hotels',
          success: 'Confirmed',
          lead: 'A hotel is never ticketed — it returns a confirmation, not a document. Instant properties confirm on payment; On-Request properties authorise the card first and wait for the operator (In review) before capture.',
          path: [
            { status: 'Draft', kind: 'neutral', note: 'cart built' },
            { status: 'Pending payment', kind: 'neutral', note: 'Instant: pay to confirm' },
            { status: 'Confirmed', kind: 'success', note: 'reservation secured · voucher posts' },
          ],
          branches: [
            { from: 'Pending payment', label: 'On-Request', nodes: [{ status: 'In review', kind: 'warn', note: 'operator decides' }, { status: 'Confirmed', kind: 'success' }], desc: 'The card is authorised, the request sits with the operator; on confirm the payment is captured and the stay is Confirmed.' },
            { from: 'In review', nodes: [{ status: 'Rejected', kind: 'danger' }], desc: 'The operator declines — the authorisation is voided, so no money is captured.' },
            { from: 'Confirmed', nodes: [{ status: 'Cancelled', kind: 'danger' }, { status: 'Refunded', kind: 'neutral' }], desc: 'Post-sale cancellation follows the rate’s rules; a refund returns captured money less penalty.' },
          ],
        },
        {
          product: 'Tours & other direct services (CIP, Transfer, SIM, Insurance)',
          success: 'Confirmed',
          lead: 'Direct-service products issue a voucher/confirmation, not a ticket, so they complete at «Confirmed». A tour bundles a flight + hotel but is sold at one supplier-independent sale price — one order, one voucher. (CIP, Transfer, SIM and Insurance are modelled but not yet live.)',
          path: [
            { status: 'Draft', kind: 'neutral', note: 'cart built' },
            { status: 'Pending payment', kind: 'neutral' },
            { status: 'Confirmed', kind: 'success', note: 'service secured · voucher posts' },
          ],
          branches: [
            { from: 'Pending payment', nodes: [{ status: 'Failed', kind: 'danger' }], desc: 'Payment is declined — the service is not confirmed.' },
            { from: 'Confirmed', nodes: [{ status: 'Cancelled', kind: 'danger' }, { status: 'Refunded', kind: 'neutral' }], desc: 'Post-sale cancellation and refund, subject to the product’s cancellation rules.' },
          ],
        },
        {
          product: 'Visa',
          success: 'Approved',
          lead: 'A visa is decided, not ticketed or confirmed: the application is submitted, sits under review, then is either approved or refused. «Approved» is visa’s success state — the same "sale is real" milestone as Confirmed, and it posts the sale voucher. It reduces to three states: In review → Approved / Rejected.',
          path: [
            { status: 'Draft', kind: 'neutral', note: 'application built' },
            { status: 'Pending payment', kind: 'neutral' },
            { status: 'In review', kind: 'warn', note: 'under processing' },
            { status: 'Approved', kind: 'success', note: 'visa granted · voucher posts' },
          ],
          branches: [
            { from: 'In review', nodes: [{ status: 'Rejected', kind: 'danger' }], desc: 'The application is refused — no voucher. A payment failure or a cancellation on a visa also collapses to Rejected.' },
          ],
        },
      ],
    },
    examples: [
      {
        title: 'A flight that completes',
        rows: [
          { label: 'Cart built', value: 'Draft' },
          { label: 'Seat held (PNR)', value: 'Held', accent: 'info' },
          { label: 'Customer pays', value: 'Pending → paid' },
          { label: 'Ticket issued', value: 'Ticketed', strong: true, accent: 'success' },
        ],
        note: 'The order becomes accounting-eligible the instant it is Ticketed — the sale voucher drafts from that event. Trains and buses follow the same path.',
      },
      {
        title: 'An On-Request hotel',
        rows: [
          { label: 'Booked, card authorised', value: 'Pending payment' },
          { label: 'Operator reviews the request', value: 'In review', accent: 'info' },
          { label: 'Operator confirms → capture', value: 'Confirmed', strong: true, accent: 'success' },
        ],
        note: 'If the operator rejects, the status goes Rejected and the authorisation is voided — no money is captured. An Instant property skips In review and confirms on payment.',
      },
      {
        title: 'A tour (bundled sale)',
        rows: [
          { label: 'Package booked', value: 'Pending payment' },
          { label: 'Confirmed — voucher issued', value: 'Confirmed', strong: true, accent: 'success' },
        ],
        note: 'A tour packages a flight + hotel but posts as one supplier-independent sale at a single price — Confirmed, never Ticketed.',
      },
    ],
    related: ['booking-detail', 'queue', 'refunds', 'counter'],
  },

  'booking-detail': {
    title: 'Booking detail',
    tag: 'Order record',
    tagline: 'The full five-layer record of one order — items, money, payments, documents and actions.',
    business: {
      lead: 'A booking is modelled in five layers — Order → Item → Supplier reference → Document → Coupon — so every fare, tax, fee and refund attributes exactly to the thing it belongs to. This screen is where one order is understood and serviced end to end.',
      points: [
        { title: 'Five layers, exact attribution', desc: 'Money rolls up from coupons and documents to items and then the order, so you always know who owes what.' },
        { title: 'Cancel at every level', desc: 'Cancel a single coupon, a document, an item, or the whole order — always with a quote before any money moves.' },
        { title: 'On-Request lives here', desc: 'A pending (In-review) hotel request shows a Confirm / Reject bar — Confirm captures the authorised payment, Reject voids it.' },
      ],
    },
    software: {
      lead: 'A tabbed record with per-level financials and the post-sale actions.',
      points: [
        { title: 'Items', desc: 'The itemised breakdown — each product as an item with its segments / rooms, passengers and status.' },
        { title: 'Financials', desc: 'The money matrix: net, markups, taxes, fees, discounts, commissions and margin at each level.' },
        { title: 'Payments', desc: 'Payment legs (authorised / captured / refunded / voided) and the balance due.' },
        { title: 'Documents', desc: 'Issued supplier documents; a flight e-ticket opens the print-ready, bilingual Flight Ticket Voucher, and agency scans can be attached.' },
        { title: 'Charges', desc: 'Post-booking charges — add fees, ancillaries, penalties or discounts, and structural amendments (add passenger / item, name correction) with VAT.' },
        { title: 'Tickets', desc: 'Support tickets raised on this booking’s items between the agency and the platform desk.' },
        { title: 'Cancel & refund', desc: 'Quote and execute a cancellation at any level; refunds route into the Refunds & Voids approval workflow.' },
      ],
    },
    examples: [
      {
        title: 'How the money rolls up (one hotel item)',
        rows: [
          { label: 'Supplier net', value: '7,400,000' },
          { label: '+ Retail markup', value: '+1,110,000', accent: 'info' },
          { label: '+ Ancillaries & VAT', value: '+990,000', accent: 'success' },
          { label: 'Sell total', value: '9,500,000', strong: true, accent: 'accent' },
          { label: '− Paid', value: '9,500,000' },
          { label: 'Balance due', value: '0', strong: true },
        ],
        note: 'The order total is the sum of its items; each item’s sell total is its net plus the markup layers, charges and taxes attributed to it.',
      },
    ],
    related: ['bookings', 'refunds', 'queue'],
  },

  queue: {
    title: 'Reservation queue',
    tag: 'PNR holds',
    tagline: 'PNRs holding inventory that still need ticketing or supplier action.',
    business: {
      lead: 'A held PNR reserves inventory but hasn’t been ticketed yet — it’s a promise on a deadline. The queue surfaces these so staff act before the hold expires and the seat or room is auto-released back to the supplier.',
      points: [
        { title: 'Holds are perishable', desc: 'Unticketed PNRs auto-cancel when their hold window lapses (a sweeper runs every 60 seconds), freeing the inventory.' },
        { title: 'Beat the clock', desc: 'Time-to-ticket is the metric that matters — issue in time or the reservation is lost.' },
        { title: 'Not only ticketing', desc: 'Some rows are waiting on a supplier (e.g. an On-Request confirmation), not just on your issuing action.' },
      ],
    },
    software: {
      lead: 'A time-ordered worklist of holds with their deadlines.',
      points: [
        { title: 'See what’s expiring', desc: 'Each row shows its hold expiry, so the most urgent float to the top.' },
        { title: 'Act on a row', desc: 'Open the booking to issue / ticket or take the required supplier action.' },
        { title: 'Auto-cancel is automatic', desc: 'Expired holds are released by the background sweeper and drop off the queue.' },
      ],
    },
    related: ['bookings', 'booking-detail'],
  },

  refunds: {
    title: 'Refunds & Voids',
    tag: 'Post-sale money',
    tagline: 'The controlled workflow for giving money back — cancellations, refunds and voids.',
    business: {
      lead: 'When a booking is cancelled the money has to move back correctly and under control. Refunds & Voids is where requests are raised and approved, so refunds follow the fare rules and nothing leaves without a check.',
      points: [
        { title: 'Initiate vs approve', desc: 'Raising a refund and approving it are separate steps — approval is the money-moves gate, held behind its own permission.' },
        { title: 'Void vs refund', desc: 'A void cancels an authorisation that was never captured (nothing was charged); a refund returns captured money, less any penalty.' },
        { title: 'Rules, not guesswork', desc: 'Amounts follow the fare / cancellation rules and any penalty; the request records the breakdown.' },
      ],
    },
    software: {
      lead: 'A queue of refund / void requests with their status and amounts.',
      points: [
        { title: 'Work the queue', desc: 'See pending requests with their amount, penalty and reason.' },
        { title: 'Approve or reject', desc: 'Approve to move the money (refund the capture / void the authorisation) or reject with a reason.' },
        { title: 'Traceable', desc: 'Every decision is attributed and tied back to the originating booking.' },
      ],
    },
    related: ['booking-detail', 'bookings'],
  },

  counter: {
    title: 'Counter',
    tag: 'Cash & POS',
    tagline: 'Cashier shifts, cash and POS movements, and close-of-day reconciliation.',
    business: {
      lead: 'For agencies selling over a physical counter, this is the cash-control layer: it tracks who is on shift, every cash and card movement, and squares the till at day’s end so takings match the record.',
      points: [
        { title: 'Accountability per shift', desc: 'Opening and closing a shift ties every movement to a cashier and a session.' },
        { title: 'Cash discipline', desc: 'Recording cash and POS movements as they happen is what makes end-of-day reconciliation possible.' },
        { title: 'Reconcile, don’t guess', desc: 'Close-of-day compares expected against counted, so a discrepancy surfaces immediately.' },
      ],
    },
    software: {
      lead: 'A dashboard for the current shift with its movements and reconciliation.',
      points: [
        { title: 'Open / close a shift', desc: 'Start a session to begin recording; close it to reconcile.' },
        { title: 'Record movements', desc: 'Log cash in / out and POS transactions during the shift.' },
        { title: 'Reconcile at close', desc: 'Compare expected against counted and record the variance.' },
      ],
    },
    related: ['bookings', 'refunds'],
  },
};

/**
 * Locale overrides for page help, keyed by language then page key. When the app
 * language matches and an entry exists, the drawer renders it instead of the
 * English default in {@link PAGE_HELP}; anything not translated falls back to
 * English. Technical tokens the UI shows verbatim — the layer names (Wholesale
 * / Distribution / Retail), adjustment types (MARKUP, DISCOUNT, SERVICE_FEE,
 * REBATE) and SIGN — are kept as-is so the help matches the on-screen labels;
 * only the surrounding prose is translated.
 *
 * The full translations (all 56 pages × fr/fa/tr/ru/ar) live in ./l10n/*, split
 * per language × page-group and merged in ./l10n/index. Re-exported here so the
 * existing `PAGE_HELP_L10N` import path keeps working.
 */
import { PAGE_HELP_L10N } from './l10n';
export { PAGE_HELP_L10N };

/** Resolve the page help for a key in the given language, falling back to the
 *  English default when there is no localized override. */
export function resolvePageHelp(key: string, lang: string): PageHelp | undefined {
  return PAGE_HELP_L10N[lang]?.[key] ?? PAGE_HELP[key];
}

/** Pretty labels for the "related pages" quick links. */
export const PAGE_HELP_LABEL: Record<string, string> = {
  customers: 'Customers',
  'customer-groups': 'Customer Groups',
  loyalty: 'Loyalty Club',
  agencies: 'Agencies',
  agents: 'Sub-Agencies',
  markup: 'Markup & Rules',
  'partner-access': 'Partner Access',
  companies: 'Companies',
  'sale-channels': 'Sale channels',
  'channel-access': 'Channel access',
  'sign-access': 'SIGN access',
  signs: 'SIGNs',
  branches: 'Branches',
  'agency-groups': 'Agency groups',
  'agency-onboarding': 'Onboard agency',
  'agency-kyc': 'KYC review',
  'agency-profile': 'Agency profile',
  'contract-templates': 'Contract templates',
  'reference-data': 'Reference Data',
  'data-simulator': 'Data Simulator',
  rbac: 'Roles & Access',
  users: 'Users',
  settings: 'Settings',
  issues: 'Issue Tracker',
  'my-issues': 'My Reported Issues',
  'supply-properties': 'Hotel properties',
  'supply-property-detail': 'Hotel Property',
  'flights-search': 'Flights',
  'flights-results': 'Flight results',
  'flights-manual': 'Flight manual entry',
  'flights-passengers': 'Flight passengers',
  'flights-seats': 'Seat selection',
  'flights-payment': 'Flight payment',
  'flights-ticket': 'Flight ticket',
  'hotels-search': 'Hotels',
  'hotels-results': 'Hotel results',
  'hotels-ticket': 'Hotel voucher',
  tours: 'Tours',
  'tours-new': 'Tour package builder',
  'trains-search': 'Trains',
  'trains-results': 'Train results',
  'trains-manual': 'Train manual entry',
  'trains-passengers': 'Train seats & pax',
  'trains-payment': 'Train payment',
  'trains-ticket': 'Train ticket',
  'buses-search': 'Buses',
  visa: 'Visa',
  cip: 'CIP',
  ground: 'Ground Transport',
  sim: 'SIM Cards',
  insurance: 'Travel Insurance',
  bookings: 'All Bookings',
  'booking-detail': 'Booking detail',
  queue: 'Reservation queue',
  refunds: 'Refunds & Voids',
  counter: 'Counter',
};

/** Map a router URL to its page-help key (the first path segment), or null when
 *  the current page has no help entry. */
export function pageHelpKeyForUrl(url: string): string | null {
  const seg = (url.split('?')[0].split('#')[0].split('/')[1] || '').toLowerCase();
  return PAGE_HELP[seg] ? seg : null;
}
